import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AppShellSkeleton } from "./components/ui/AppShellSkeleton";
import Layout from "./components/Layout";
import LayoutV2 from "./components/LayoutV2";
import MobileLayout from "./components/mobile/MobileLayout";
import MobileRouteFallback from "./components/mobile/MobileRouteFallback";
import { AuthProvider } from "./contexts/AuthContext";
import { ReturnPathTracker } from "./utils/loginReturn";
import { RoleProvider } from "./contexts/RoleContext";
import { useEffectiveRole } from "./hooks/useEffectiveRole";
import { useAuth } from "./contexts/AuthContext";
import { useTranslation } from "react-i18next";
import Unauthorized from "./components/common/Unauthorized";
import ProtectedRoute from "./components/common/ProtectedRoute";
import { WorkspaceProvider } from "./features/workspace";
import { StepUpModal } from "./components/auth/StepUpModal";
import { registerStepUpModalOpener } from "./services/api.service";

const Dashboard = React.lazy(() => import("./components/dashboard/Dashboard"));
const TestAgentChatV5 = React.lazy(() => import("./pages/TestAgentChatV5"));
const BellaVibePage = React.lazy(() => import("./pages/BellaVibePage"));
const BellaVibeV3Page = React.lazy(() => import("./pages/BellaVibeV3Page"));
const UserManagement = React.lazy(() => import("./components/admin/UserManagement"));
const UserDetail = React.lazy(() => import("./components/admin/UserDetail"));
const UserModules = React.lazy(() => import("./components/admin/UserModules"));
const UserAgents = React.lazy(() => import("./components/admin/UserAgents"));
const UserOrganizations = React.lazy(() => import("./components/admin/UserOrganizations"));
const UserProfile = React.lazy(() => import("./components/profile/UserProfile"));
const UserCredentialWallet = React.lazy(() => import("./components/profile/UserCredentialWallet"));
const SubscribePage = React.lazy(() => import("./pages/SubscribePage"));
const SubscribeSuccessPage = React.lazy(() => import("./pages/SubscribeSuccessPage"));
const OAuthDevicePage = React.lazy(() => import("./pages/OAuthDevicePage"));
const OAuthAuthorizePage = React.lazy(() => import("./pages/OAuthAuthorizePage"));
const AgentManagement = React.lazy(() => import("./components/admin/AgentManagement"));
const KnowledgeIngestion = React.lazy(() => import("./components/ingestion/KnowledgeIngestion"));
const Settings = React.lazy(() => import("./components/admin/Settings"));
const Menu = React.lazy(() => import("./components/admin/Menu"));
const GeneralSettingsPage = React.lazy(() => import("./pages/settings/GeneralSettingsPage"));
const EmailOpsPage = React.lazy(() => import("./pages/settings/EmailOpsPage"));
const SmartAgentWizardPage = React.lazy(() => import("./pages/settings/SmartAgentWizardPage"));
const FlowDesignerAdminPage = React.lazy(() => import("./pages/settings/FlowDesignerAdminPage"));
const VoiceAdminPage = React.lazy(() => import("./pages/settings/VoiceAdminPage"));
const WorkersAdminPage = React.lazy(() => import("./pages/settings/WorkersAdminPage"));
const SystemHealthNocPage = React.lazy(() => import("./pages/settings/SystemHealthNocPage"));
const A2AMessageBusPage = React.lazy(() => import("./pages/settings/A2AMessageBusPage"));
const FinancialIntelligencePage = React.lazy(() => import("./pages/settings/FinancialIntelligencePage"));
const BudgetPlanningPage = React.lazy(() => import("./pages/settings/BudgetPlanningPage"));
const QuantumReadinessPage = React.lazy(() => import("./pages/settings/QuantumReadinessPage"));
const MobileQuantumReadinessPage = React.lazy(() => import("./pages/mobile/MobileQuantumReadinessPage"));
const MobileCollectionsPage = React.lazy(() => import("./pages/mobile/MobileCollectionsPage"));
const HomologationPage = React.lazy(() => import("./pages/settings/HomologationPage"));
const MobileHomologationPage = React.lazy(() => import("./pages/mobile/MobileHomologationPage"));
const ApprovalWorkflowsPage = React.lazy(() => import("./pages/settings/ApprovalWorkflowsPage"));
const IoTTelemetryPage = React.lazy(() => import("./pages/settings/IoTTelemetryPage"));
const ReferenceValuesPage = React.lazy(() => import("./pages/settings/ReferenceValuesPage"));
const MobileReferenceValuesPage = React.lazy(() => import("./pages/mobile/MobileReferenceValuesPage"));
const MobileSamplesPage = React.lazy(() => import("./pages/mobile/MobileSamplesPage"));
const MobileQuotingPage = React.lazy(() => import("./pages/mobile/MobileQuotingPage"));
const MobilePipelinePage = React.lazy(() => import("./pages/mobile/MobilePipelinePage"));
const DriveMonitorSettingsPage = React.lazy(() => import("./pages/settings/DriveMonitorSettingsPage"));
const ChannelsPage = React.lazy(() => import("./pages/settings/ChannelsPage"));
const MobileChannelsPage = React.lazy(() => import("./pages/mobile/MobileChannelsPage"));
const BankingPage = React.lazy(() => import("./pages/settings/BankingPage"));
const ShiftSensePage = React.lazy(() => import("./pages/settings/ShiftSensePage"));
const CouncilPage = React.lazy(() => import("./pages/settings/CouncilPage"));
const CouncilSpecialistFormPage = React.lazy(() => import("./pages/settings/CouncilSpecialistFormPage"));
const CouncilSpecialistDetailPage = React.lazy(() => import("./pages/settings/CouncilSpecialistDetailPage"));
const ProductFactoryPage = React.lazy(() => import("./pages/settings/ProductFactoryPage"));
const MobileISOPage = React.lazy(() => import("./pages/mobile/MobileISOPage"));
const MobileIoTTelemetryPage = React.lazy(() => import("./pages/mobile/MobileIoTTelemetryPage"));
const MobileIoTDevicesPage = React.lazy(() => import("./pages/mobile/MobileIoTDevicesPage"));
const MobileIoTDeviceDetailPage = React.lazy(() => import("./pages/mobile/MobileIoTDeviceDetailPage"));
const MobileIoTEventsPage = React.lazy(() => import("./pages/mobile/MobileIoTEventsPage"));
const ToolWorkbenchPage = React.lazy(() => import("./pages/ToolWorkbenchPage"));
const MyMemoryPage = React.lazy(() => import("./pages/settings/MyMemoryPage"));
const CompliancePage = React.lazy(() => import("./pages/settings/CompliancePage"));
const SkillsPage = React.lazy(() => import("./pages/settings/SkillsPage"));
const CaptureEnginePage = React.lazy(() => import("./pages/settings/CaptureEnginePage"));
const MobileCapturePage = React.lazy(() => import("./pages/mobile/MobileCapturePage"));
const MobileSceneSymbolsPage = React.lazy(() => import("./pages/mobile/MobileSceneSymbolsPage"));
const SceneSymbolsPage = React.lazy(() => import("./pages/settings/SceneSymbolsPage"));
const LlmVisibilitySuitePage = React.lazy(() => import("./features/llmVisibility").then(m => ({ default: m.LlmVisibilitySuitePage })));
const MCPGatewayPage = React.lazy(() => import("./pages/settings/MCPGatewayPage"));
const OTPConfigPage = React.lazy(() => import("./pages/settings/OTPConfigPage"));
const ValidationRulesPage = React.lazy(() => import("./pages/settings/ValidationRulesPage"));
const ToolLearningPage = React.lazy(() => import("./pages/settings/ToolLearningPage"));
const FaceVerifyPublic = React.lazy(() => import("./pages/FaceVerifyPublic"));

function FaceVerifyTokenRedirect() {
  const { token } = useParams();
  return <Navigate to={`/face-verify-web/${token}`} replace />;
}
const MobileJobsPage = React.lazy(() => import("./pages/mobile/MobileJobsPage"));
const MobileNavigatorPage = React.lazy(() => import("./pages/mobile/MobileNavigatorPage"));
const MobileClerksEcosystemPage = React.lazy(() => import("./pages/mobile/MobileClerksEcosystemPage"));
const MobileA2AMessageBusPage = React.lazy(() => import("./pages/mobile/MobileA2AMessageBusPage"));
const MobileAutoDemoPage = React.lazy(() => import("./pages/mobile/MobileAutoDemoPage"));
const MobileNotificationsPage = React.lazy(() => import("./pages/mobile/MobileNotificationsPage"));
const MobileProfilePage = React.lazy(() => import("./pages/mobile/MobileProfilePage"));
const MobileControlCenterPage = React.lazy(() => import("./pages/mobile/MobileControlCenterPage"));
const MobileBillingPage = React.lazy(() => import("./pages/mobile/MobileBillingPage"));
const MobileWalletPage = React.lazy(() => import("./pages/mobile/MobileWalletPage"));
const MobileWorkspacePage = React.lazy(() => import("./pages/mobile/MobileWorkspacePage"));
const MobileDashboardPage = React.lazy(() => import("./pages/mobile/MobileDashboardPage"));
const MobileSmartAgentsPage = React.lazy(() => import("./pages/mobile/MobileSmartAgentsPage"));
const MobileCampaignsPage = React.lazy(() => import("./pages/mobile/MobileCampaignsPage"));
const MobileSegmentationPage = React.lazy(() => import("./pages/mobile/MobileSegmentationPage"));
const MobileSkillsPage = React.lazy(() => import("./pages/mobile/MobileSkillsPage"));
const MobileSentinel360Page = React.lazy(() => import("./pages/mobile/MobileSentinel360Page"));
const MobileAnalyticsPage = React.lazy(() => import("./pages/mobile/MobileAnalyticsPage"));
const MobileTrainingPage = React.lazy(() => import("./pages/mobile/MobileTrainingPage"));
const MobileAnalystStudioPage = React.lazy(() => import("./pages/mobile/MobileAnalystStudioPage"));
const AnalystStudioPage = React.lazy(() => import("./pages/settings/AnalystStudioPage"));
const CostEstimatorPage = React.lazy(() => import("./pages/settings/CostEstimatorPage"));
const QuotingPage = React.lazy(() => import("./pages/settings/QuotingPage"));
const PersonaProfilePage = React.lazy(() => import("./features/persona-profile/PersonaProfilePage"));
const MobilePersonaProfilePage = React.lazy(() => import("./pages/mobile/MobilePersonaProfilePage"));
const SitesPage = React.lazy(() => import("./features/sites/SitesPage"));
const DigitalAssetsPage = React.lazy(() => import("./features/digital-assets/DigitalAssetsPage"));
const DigitalMentorPage = React.lazy(() => import("./features/digital-mentor/DigitalMentorPage"));
const MobileDigitalMentorPage = React.lazy(() => import("./pages/mobile/MobileDigitalMentorPage"));
const MeetingAgentPage = React.lazy(() => import("./features/meetings/MeetingAgentPage"));
const MobileMeetingAgentPage = React.lazy(() => import("./pages/mobile/MobileMeetingAgentPage"));
const MobileSitesPage = React.lazy(() => import("./pages/mobile/MobileSitesPage"));
const MobileDigitalAssetsPage = React.lazy(() => import("./pages/mobile/MobileDigitalAssetsPage"));
const MobileDataIntakePage = React.lazy(() => import("./pages/mobile/MobileDataIntakePage"));
const DataIntakePage = React.lazy(() => import("./pages/settings/DataIntakePage"));
const MobileProcessStudioPage = React.lazy(() => import("./pages/mobile/MobileProcessStudioPage"));
const MobileProcessDetailPage = React.lazy(() => import("./pages/mobile/MobileProcessDetailPage"));
const MobileProcessBuilderPage = React.lazy(() => import("./pages/mobile/MobileProcessBuilderPage"));
const AASetupWizardPage = React.lazy(() => import("./features/aa-setup-wizard/index"));
const MobileAASetupStubPage = React.lazy(() => import("./pages/mobile/MobileAASetupStubPage"));
const CommerceDashboardPage = React.lazy(() => import("./features/commerce/CommerceDashboardPage"));
const StorefrontPage = React.lazy(() => import("./pages/storefront/StorefrontPage"));
const PurchaseInputPage = React.lazy(() => import("./pages/storefront/PurchaseInputPage"));
const MobileCommercePage = React.lazy(() => import("./pages/mobile/MobileCommercePage"));
const MobileServiceKeysPage = React.lazy(() => import("./pages/mobile/MobileServiceKeysPage"));
const ProcessStudioPage = React.lazy(() => import("./features/process-studio/ProcessStudioPage"));
const ProcessBuilderPage = React.lazy(() => import("./pages/ProcessBuilderPage"));
const StudioPage = React.lazy(() => import("./pages/StudioPage"));
const ProcessDetailPage = React.lazy(() => import("./pages/ProcessDetailPage"));
const MDGAdminPage = React.lazy(() => import("./pages/settings/MDGAdminPage"));
const CastorPage = React.lazy(() => import("./pages/settings/CastorPage"));
const CodeRepoAdminPage = React.lazy(() => import("./pages/settings/CodeRepoAdminPage"));
const CodeRepoChatPage = React.lazy(() => import("./pages/CodeRepoChatPage"));
const MobileCodeRepoChatPage = React.lazy(() => import("./pages/mobile/MobileCodeRepoChatPage"));
const YoriAdminPage = React.lazy(() => import("./pages/settings/YoriAdminPage"));
const OnboardingAdminPage = React.lazy(() => import("./pages/settings/OnboardingAdminPage"));
const CluRagTrainingPage = React.lazy(() => import("./pages/settings/CluRagTrainingPage"));
const ProspectCatchPage = React.lazy(() => import("./pages/settings/ProspectCatchPage"));
const DemoVideoAdminPage = React.lazy(() => import("./pages/settings/DemoVideoAdminPage"));
const ExcelExportSettingsPage = React.lazy(() => import("./pages/settings/ExcelExportSettingsPage"));
const ApiKeysAdminPage = React.lazy(() => import("./components/admin/apiKeys/ApiKeysAdminPage"));
const MyApiKeysPage = React.lazy(() => import("./components/admin/apiKeys/MyApiKeysPage"));
const RoleManagement = React.lazy(() => import("./components/admin/RoleManagement"));
const DynamicDashboard = React.lazy(() => import("./pages/DynamicDashboard"));
const AgentActionPlansList = React.lazy(() => import("./components/aab/AgentActionPlansList"));
const AgentActionPlanDetail = React.lazy(() => import("./components/aab/AgentActionPlanDetail"));
const AgentActionPlanBuilder = React.lazy(() => import("./components/aab/AgentActionPlanBuilder"));
const PatternsAdminPage = React.lazy(() => import("./components/admin/patterns/PatternsAdminPage"));
const PatternEditor = React.lazy(() => import("./components/admin/patterns/PatternEditor"));
const EntityExtractionPatternEditor = React.lazy(() => import("./components/admin/PatternEditor"));
const PatternPlayground = React.lazy(() => import("./components/admin/patterns/PatternPlayground"));
const PatternEventsExplorer = React.lazy(() => import("./components/admin/patterns/PatternEventsExplorer"));
const StreamsMonitor = React.lazy(() => import("./components/admin/patterns/StreamsMonitor"));

// Component to handle default route redirection based on user role
function DefaultRedirect() {
  const { t } = useTranslation("common");
  const { isSuperUser } = useEffectiveRole();
  const { loading } = useAuth();

  // Wait for auth to load before redirecting
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">{t("app_loading")}</div>;
  }
  
  return <Navigate to="/clu-ng" replace />;
}
const FirebaseLogin = React.lazy(() => import("./components/auth/FirebaseLogin"));
const PublicOnboardingWizard = React.lazy(() => import("./components/public/PublicOnboardingWizard"));
const ISOMarketplacePage = React.lazy(() => import("./pages/public/ISOMarketplacePage"));
const ISOOnboardingPage = React.lazy(() => import("./pages/public/ISOOnboardingPage"));
const NexorLandingPage = React.lazy(() => import("./pages/public/NexorLandingPage"));
const SiteRedirectPage = React.lazy(() => import("./pages/public/SiteRedirectPage"));
const InvoicePublicPage = React.lazy(() => import("./pages/public/InvoicePublicPage"));
const ArtifactViewPage = React.lazy(() => import("./pages/public/ArtifactViewPage"));
const MeetingScreenPage = React.lazy(() => import("./pages/public/MeetingScreenPage"));
const ShellPage = React.lazy(() => import("./pages/shell/ShellPage"));
const AgentChatV5 = React.lazy(() => import("./components/chat/AgentChatV5"));
const SmartDataHelper = React.lazy(() => import("./components/chat/SmartDataHelper"));
const AnalyticsChatV1 = React.lazy(() => import("./components/chat/AnalyticsChatV1"));
const AnalyticsPage = React.lazy(() => import("./pages/AnalyticsPage"));
const IntegratedAssistant = React.lazy(() => import("./components/integrated/IntegratedAssistant.jsx"));

const OrganizationManagement = React.lazy(() => import("./components/admin/OrganizationManagement"));
const OrganizationModules = React.lazy(() => import("./components/admin/OrganizationModules"));
const OrganizationUsers = React.lazy(() => import("./components/admin/OrganizationUsers"));
const OrganizationClerksPage = React.lazy(() => import("./pages/admin/OrganizationClerksPage"));
const OrganizationProfilePage = React.lazy(() => import("./pages/admin/OrganizationProfilePage"));

const ClerksEcosystemPage = React.lazy(() => import("./pages/admin/ClerksEcosystemPage"));
const ClerksChainPage = React.lazy(() => import("./pages/admin/ClerksChainPage"));

const PoiesisRequestDetail = React.lazy(() => import("./features/poiesis/PoiesisRequestDetail"));
const PoiesisAdminPage = React.lazy(() => import("./pages/settings/PoiesisAdminPage"));
const MobilePoiesisPage = React.lazy(() => import("./pages/mobile/MobilePoiesisPage"));
const PublicModulePage = React.lazy(() => import("./pages/PublicModulePage"));
const InternalModulePage = React.lazy(() => import("./pages/InternalModulePage"));

const ConnectorsLibrary = React.lazy(() => import("./components/admin/ConnectorsLibrary"));
const ConnectorCatalogPage = React.lazy(() => import("./pages/settings/ConnectorCatalogPage"));
const EcosystemVisibilityPage = React.lazy(() => import("./pages/settings/EcosystemVisibilityPage"));
const ToolCardViewer = React.lazy(() => import("./components/admin/ToolCardViewer"));
const ConnectorRunnerPage = React.lazy(() => import("./pages/admin/ConnectorRunnerPage"));

const ModuleManager = React.lazy(() => import("./components/admin/ModuleManager"));
const SlackIntegration = React.lazy(() => import("./components/admin/SlackIntegration"));
const SQLTestPanel = React.lazy(() => import("./components/admin/SQLTestPanel"));

const RedisAdminPage = React.lazy(() => import("./pages/admin/RedisAdminPage"));

const SettingsManager = React.lazy(() => import("./components/admin/SettingsManager"));
const HyperCoreAdmin = React.lazy(() => import("./components/admin/HyperCoreAdmin"));
const AtomicHyperPersonalizationAdmin = React.lazy(() => import("./components/admin/AtomicHyperPersonalizationAdmin"));
const KnowledgeSourceRegistryPage = React.lazy(() => import("./pages/settings/KnowledgeSourceRegistryPage"));
const MobileKnowledgeSourcesPage = React.lazy(() => import("./pages/mobile/MobileKnowledgeSourcesPage"));
const MobileVideoPipelinePage = React.lazy(() => import("./pages/mobile/MobileVideoPipelinePage"));
const MobileVideoIngestionPage = React.lazy(() => import("./pages/mobile/MobileVideoIngestionPage"));
const SemanticIndexPage = React.lazy(() => import("./pages/settings/SemanticIndexPage"));
const MobileSemanticIndexPage = React.lazy(() => import("./pages/mobile/MobileSemanticIndexPage"));
const MobileToolWorkbenchPage = React.lazy(() => import("./pages/mobile/MobileToolWorkbenchPage"));
const MobileLlmVisibilityPage = React.lazy(() => import("./pages/mobile/MobileLlmVisibilityPage"));
const MobileDriveMonitorStatus = React.lazy(() => import("./pages/mobile/MobileDriveMonitorStatus"));
const MobileBankingPage = React.lazy(() => import("./pages/mobile/MobileBankingPage"));
const MobileGCSFileManagerPage = React.lazy(() => import("./pages/mobile/MobileGCSFileManagerPage"));
const MobileShiftSensePage = React.lazy(() => import("./pages/mobile/MobileShiftSensePage"));
const MobileCouncilPage = React.lazy(() => import("./pages/mobile/MobileCouncilPage"));
const MobileShiftSenseEventsPage = React.lazy(() => import("./pages/mobile/MobileShiftSenseEventsPage"));
const MobileShiftSenseStaffPage = React.lazy(() => import("./pages/mobile/MobileShiftSenseStaffPage"));
const VoiceCallsPage = React.lazy(() => import("./pages/settings/VoiceCallsPage"));
const MobileVoiceCallsPage = React.lazy(() => import("./pages/mobile/MobileVoiceCallsPage"));
const LocalRuntimePage = React.lazy(() => import("./pages/settings/LocalRuntimePage"));
const MobileLocalRuntimePage = React.lazy(() => import("./pages/mobile/MobileLocalRuntimePage"));

const GCSFileManager = React.lazy(() => import("./components/admin/GCSFileManager"));
const ArtifactTemplatesPage = React.lazy(() => import("./pages/settings/ArtifactTemplatesPage"));
const UserDrivePage = React.lazy(() => import("./pages/UserDrivePage"));
const MobileUserDrivePage = React.lazy(() => import("./pages/mobile/MobileUserDrivePage"));
const MobileDocumentPipelineRunPage = React.lazy(() => import("./pages/mobile/MobileDocumentPipelineRunPage"));

const SQLConsoleServer = React.lazy(() => import("./components/admin/SQLConsoleServer"));

const RLSManagement = React.lazy(() => import("./components/admin/RLSManagement"));

const SQLQueriesVault = React.lazy(() => import("./components/admin/SQLQueriesVault"));

const ToolsAudit = React.lazy(() => import("./pages/admin/ToolsAudit"));

// Import Worker Metrics Dashboard - Now integrated in WorkersAdminPage
// const WorkerMetricsDashboard = React.lazy(() => import("./pages/admin/WorkerMetricsDashboard"));

const TablesManagement = React.lazy(() => import("./components/admin/TablesManagement"));
const TablesPage = React.lazy(() => import("./pages/admin/TablesPage"));
const EmergencyTestPage = React.lazy(() => import("./pages/admin/EmergencyTestPage"));
const SimpleTablesPage = React.lazy(() => import("./pages/admin/SimpleTablesPage"));

const OrchestratorDebugDashboard = React.lazy(() => import("./components/admin/OrchestratorDebugDashboard"));

const CIECampaignManager = React.lazy(() => import("./components/admin/CIECampaignManager"));

const DynamicPromptVariablesManager = React.lazy(() => import("./components/admin/DynamicPromptVariablesManager"));
const CLUAdminPage = React.lazy(() => import("./pages/admin/CLUAdminPage"));
const ObservationAdminPage = React.lazy(() => import("./pages/admin/ObservationAdminPage"));
const SchedulerControlPanel = React.lazy(() => import("./components/admin/SchedulerControlPanel"));
const MemoryLearningAdminPage = React.lazy(() => import("./pages/admin/MemoryLearningAdminPage"));
const ControlTowerPage = React.lazy(() => import("./pages/admin/ControlTowerPage"));
const DeployEnginePage = React.lazy(() => import("./pages/admin/DeployEnginePage"));
const MobileDeploysPage = React.lazy(() => import("./pages/mobile/MobileDeploysPage"));
const GraphMemoryAdminPage = React.lazy(() => import("./pages/admin/GraphMemoryAdminPage"));
const ControlCenter = React.lazy(() => import("./pages/ControlCenter"));
const ChannelsAdminPage = React.lazy(() => import("./pages/admin/ChannelsAdminPage.jsx"));
const ProvidersAdminPage = React.lazy(() => import("./pages/admin/ProvidersAdminPage.jsx"));
const TMLAdmin = React.lazy(() => import("./components/admin/TMLAdmin.jsx"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const WidgetBotsAdminPage = React.lazy(() => import("./pages/admin/WidgetBotsAdminPage.jsx"));
const WidgetBotConfigPage = React.lazy(() => import("./pages/admin/WidgetBotConfigPage.jsx"));
const ConversationsPage = React.lazy(() => import("./pages/admin/ConversationsPage.jsx"));
const MobileConversationsPage = React.lazy(() => import("./pages/mobile/MobileConversationsPage.jsx"));
// Desktop Helpdesk Tickets page removed — merged into the Conversations
// "Helpdesk Tickets" tab (features/conversations/components/HelpdeskTicketsTab.jsx).
const MobileHelpdeskTicketsPage = React.lazy(() => import("./pages/mobile/MobileHelpdeskTicketsPage.jsx"));

const SOADashboard = React.lazy(() => import("./components/admin/SOADashboard"));
const SOAConsentManager = React.lazy(() => import("./components/admin/SOAConsentManager"));
const SOAProfileManager = React.lazy(() => import("./components/admin/SOAProfileManager"));

const MonitoringDashboardLayout = React.lazy(() => import("./components/admin/monitoring/MonitoringDashboardLayout"));
const InfraOverviewDashboard = React.lazy(() => import("./components/admin/monitoring/InfraOverviewDashboard"));
const ServerDetailDashboard = React.lazy(() => import("./components/admin/monitoring/ServerDetailDashboard"));
const SAPDashboard = React.lazy(() => import("./components/admin/monitoring/SAPDashboard"));
const SAPJobMonitorDashboard = React.lazy(() => import("./components/admin/monitoring/SAPJobMonitorDashboard"));
const SAPInterfaceDashboard = React.lazy(() => import("./components/admin/monitoring/SAPInterfaceDashboard"));
const AlertManagementDashboard = React.lazy(() => import("./components/admin/monitoring/AlertManagementDashboard"));
const MonitoringAdminPage = React.lazy(() => import("./components/admin/monitoring/MonitoringAdminPage"));

const GRILiveKitConfigPage = React.lazy(() => import("./pages/admin/GRI/GRILiveKitConfigPage"));
const GRIRoomsAdminPage = React.lazy(() => import("./pages/admin/GRI/GRIRoomsAdminPage"));
const GRIRoomJoinPage = React.lazy(() => import("./pages/admin/GRI/GRIRoomJoinPage"));
const BugReportsAdminPage = React.lazy(() => import("./pages/admin/BugReportsAdminPage"));

const LLMCostDashboard = React.lazy(() => import("./components/admin/LLMCostDashboard"));

const NavigatorPerformanceMonitor = React.lazy(() => import("./components/admin/NavigatorPerformanceMonitor"));
const NavigatorAdminPage = React.lazy(() => import("./pages/admin/NavigatorAdminPage"));
const BillingAdminPage = React.lazy(() => import("./pages/admin/BillingAdminPage"));
const MyBillingPage = React.lazy(() => import("./pages/MyBillingPage"));
const UpgradeBillingPage = React.lazy(() => import("./pages/UpgradeBillingPage"));
const DashboardAdminPage = React.lazy(() => import("./pages/admin/DashboardAdminPage"));
const FBTAdminPage = React.lazy(() => import("./pages/admin/FBTAdminPage"));
const VisionAdminPage = React.lazy(() => import("./pages/admin/VisionAdminPage"));
const ConstellationPage = React.lazy(() => import("./pages/admin/GalaxiaPage"));
const MobileGalaxiaPage = React.lazy(() => import("./pages/mobile/MobileGalaxiaPage"));

const LLMModelsManager = React.lazy(() => import("./components/admin/LLMModelsManager"));

const EntityExtractionAdminPage = React.lazy(() => import("./components/admin/EntityExtractionAdminPage"));
const VideoPipelinePage = React.lazy(() => import("./pages/settings/VideoPipelinePage"));
const EntityTypeEditor = React.lazy(() => import("./components/admin/EntityTypeEditor"));
const ExtractorEditor = React.lazy(() => import("./components/admin/ExtractorEditor"));

const BigQueryConsole = React.lazy(() => import("./components/admin/BigQueryConsole"));
const CredentialsAdmin = React.lazy(() => import("./components/admin/CredentialsAdmin"));

// Import Visual Workflow Designer


// Temporary component for routes under construction
const UnderConstruction = () => (
  <div className="p-10 text-center">
    <h1 className="text-2xl text-blue-400 dark:text-blue-500 mb-4">🚧 En Construcción 🚧</h1>
    <p className="text-gray-300">Esta funcionalidad está siendo desarrollada.</p>
  </div>
);

// Temporary components for Autonomous Agents section
const AABLayout = () => {
  return (
    <div className="p-4">
      <h1 className="text-2xl text-blue-400 dark:text-blue-500 mb-4">Autonomous Agents</h1>
      <Routes>
        <Route path="plans" element={<AABPlans />} />
        <Route path="create" element={<AABCreate />} />
        <Route path="edit/:id" element={<AABEdit />} />
      </Routes>
    </div>
  );
};

const AABPlans = () => <UnderConstruction />;
const AABCreate = () => <UnderConstruction />;
const AABEdit = () => <UnderConstruction />;

const AdvancedPromptBuilderManager = React.lazy(() => import("./components/admin/AdvancedPromptBuilderManager"));
const PromptLibraryPage = React.lazy(() => import("./features/prompt-library").then(m => ({ default: m.PromptLibraryPage })));
const CLUPage = React.lazy(() => import("./pages/CLU.jsx"));
const MobileCLUPage = React.lazy(() => import("./pages/mobile/MobileCLUPage.jsx"));
const LiveAssetsPage = React.lazy(() => import("./pages/LiveAssetsPage"));
const MobileLiveAssetsPage = React.lazy(() => import("./pages/mobile/MobileLiveAssetsPage"));
const NGChatPage = React.lazy(() => import("./features/clu-ng/components/NGChatPage"));
const NGConsolePage = React.lazy(() => import("./features/clu-ng/components/NGConsolePage"));
const NGSupervisorPage = React.lazy(() => import("./features/clu-ng/components/NGSupervisorPage"));
const OperatorConsolePage = React.lazy(() => import("./features/clu-ng/components/OperatorConsolePage"));
const EcosystemExplorer = React.lazy(() => import("./features/clu/components/EcosystemExplorer"));
const FBTAppHost = React.lazy(() => import("./pages/FBTAppHost"));
const ReportBuilderDashboard = React.lazy(() => import("./components/reports/ReportBuilderDashboard.jsx"));
const FaceVerifyAdmin = React.lazy(() => import("./components/admin/FaceVerifyAdmin"));
const AutomationCenter = React.lazy(() => import("./components/admin/AutomationCenter.jsx"));
const FaceEnroll = React.lazy(() => import("./components/face/FaceEnroll"));
const FaceVerify = React.lazy(() => import("./components/face/FaceVerify"));
const FaceVerifyHome = React.lazy(() => import("./components/face/FaceVerifyHome"));
const FaceVerifyPolicyAdmin = React.lazy(() => import("./components/admin/FaceVerifyPolicyAdmin"));
const FaceReviewQueue = React.lazy(() => import("./components/face/FaceReviewQueue"));

const SHPEHome = React.lazy(() => import("./components/shpe/SHPEHome"));

const ProductRecomendatorDashboard = React.lazy(() => import("./components/productrecomendator/ProductRecomendatorDashboard"));
const ProductTypeConfigManager = React.lazy(() => import("./components/productrecomendator/ProductTypeConfigManager"));
const ProductManager = React.lazy(() => import("./components/productrecomendator/ProductManager"));
const ProductComparisonView = React.lazy(() => import("./components/productrecomendator/ProductComparisonView"));
const RecommendationBuilder = React.lazy(() => import("./components/productrecomendator/RecommendationBuilder"));
const ROICalculator = React.lazy(() => import("./components/productrecomendator/ROICalculator"));
const DocumentProcessingView = React.lazy(() => import("./components/productrecomendator/DocumentProcessingView"));
const AnalyticsDashboard = React.lazy(() => import("./components/productrecomendator/AnalyticsDashboard"));
const A2AMessageMonitor = React.lazy(() => import("./components/productrecomendator/A2AMessageMonitor"));
const A2AMessageTester = React.lazy(() => import("./components/productrecomendator/A2AMessageTester"));
const A2AConfigurationPanel = React.lazy(() => import("./components/productrecomendator/A2AConfigurationPanel"));
const ProductDetailView = React.lazy(() => import("./components/productrecomendator/ProductDetailView"));
const ProductUploadInterface = React.lazy(() => import("./components/productrecomendator/ProductUploadInterface"));
const ProductEditor = React.lazy(() => import("./components/productrecomendator/ProductEditor"));
const ComparisonHistory = React.lazy(() => import("./components/productrecomendator/ComparisonHistory"));
const DocumentManagement = React.lazy(() => import("./components/productrecomendator/DocumentManagement"));

const SmartAgentDesigner = React.lazy(() => import("./components/admin/smartagents/SmartAgentDesigner"));
const SmartAgentTestingPanel = React.lazy(() => import("./components/admin/smartagents/SmartAgentTestingPanel"));
const ProfilingAdmin = React.lazy(() => import("./components/admin/ProfilingAdmin"));
const SegmentationAdmin = React.lazy(() => import("./components/admin/SegmentationAdmin"));
const MLStudioPage = React.lazy(() => import("./features/ml-studio/MLStudioPage"));
const MobileMLStudioPage = React.lazy(() => import("./pages/mobile/MobileMLStudioPage"));

const RulesEvaluationHome = React.lazy(() => import("./components/rulesevaluation/RulesEvaluationHome"));
const RulesetList = React.lazy(() => import("./components/rulesevaluation/RulesetList"));
const RulesetCreate = React.lazy(() => import("./components/rulesevaluation/RulesetCreate"));
const RulesetCreateVisual = React.lazy(() => import("./components/rulesevaluation/RulesetCreateVisual"));
const RulesetView = React.lazy(() => import("./components/rulesevaluation/RulesetView"));
const RulesetEdit = React.lazy(() => import("./components/rulesevaluation/RulesetEdit"));
const RulesetTester = React.lazy(() => import("./components/rulesevaluation/RulesetTester"));
const RulesEvaluationDashboard = React.lazy(() => import("./components/rulesevaluation/RulesEvaluationDashboard"));
const RulesetVersionManager = React.lazy(() => import("./components/rulesevaluation/RulesetVersionManager"));
const RulesetABTesting = React.lazy(() => import("./components/rulesevaluation/RulesetABTesting"));
const TestingTools = React.lazy(() => import("./components/rulesevaluation/TestingTools"));
const ExecutionHistory = React.lazy(() => import("./components/rulesevaluation/ExecutionHistory"));
const ExecutionDetail = React.lazy(() => import("./components/rulesevaluation/ExecutionDetail"));
const AgentRulesetAssociation = React.lazy(() => import("./components/rulesevaluation/AgentRulesetAssociation"));

const SemanticDomainPage = React.lazy(() => import("./pages/SemanticDomainPage"));
const DataCopilotPage = React.lazy(() => import("./pages/DataCopilotPage"));
const SemanticModelsPage = React.lazy(() => import("./pages/settings/SemanticModelsPage"));
const TransformConfigsPage = React.lazy(() => import("./pages/settings/TransformConfigsPage"));
const DataTransformationHome = React.lazy(() => import("./components/dataTransformation/DataTransformationHome"));
const TransformConfigsList = React.lazy(() => import("./components/dataTransformation/TransformConfigsList"));
const TransformConfigView = React.lazy(() => import("./components/dataTransformation/TransformConfigView"));
const TransformConfigCreate = React.lazy(() => import("./components/dataTransformation/TransformConfigCreate"));
const TransformConfigEdit = React.lazy(() => import("./components/dataTransformation/TransformConfigEdit"));

const SentinelAdminPage = React.lazy(() => import("./pages/admin/SentinelAdminPage"));
const Sentinel360Dashboard = React.lazy(() => import("./pages/admin/Sentinel360Dashboard"));
const SentinelOpsAdminPage = React.lazy(() => import("./pages/admin/SentinelOpsAdminPage"));

const SDRAgentPage = React.lazy(() => import("./pages/admin/SDRAgentPage"));

const PMMPlaybooksPage = React.lazy(() => import("./pages/admin/PMMPlaybooksPage"));

const SurveyAdminDashboard = React.lazy(() => import("./components/survey/admin/SurveyAdminDashboard"));
const SurveyLandingPage = React.lazy(() => import("./components/survey/public/SurveyLandingPage"));
const FlowRunnerPublicPage = React.lazy(() => import("./pages/flows/FlowRunnerPublicPage"));
const MyWorkspacePage = React.lazy(() => import("./pages/settings/MyWorkspacePage"));
const AutoDemoPage = React.lazy(() => import("./pages/AutoDemoPage"));
const AutoDemoSettingsPage = React.lazy(() => import("./pages/settings/AutoDemoSettingsPage"));
const DataWatchPage = React.lazy(() => import("./pages/settings/DataWatchPage"));


function GlobalStepUpModal() {
  const [stepUpRequest, setStepUpRequest] = useState(null);

  useEffect(() => {
    registerStepUpModalOpener(({ level, onVerified, onCancelled }) => {
      setStepUpRequest({
        level,
        onVerified: () => {
          setStepUpRequest(null);
          onVerified();
        },
        onCancelled: () => {
          setStepUpRequest(null);
          onCancelled();
        },
      });
    });
  }, []);

  return <StepUpModal request={stepUpRequest} />;
}

function App() {
  return (
    <BrowserRouter>
      <ReturnPathTracker />
      <AuthProvider>
        <WorkspaceProvider>
        <RoleProvider>
          <Suspense fallback={<AppShellSkeleton />}>
          <Routes>
            {/* Auth Routes - Acceso público */}
            <Route path="/login" element={<Navigate to="/firebase-login" replace />} />
            <Route path="/firebase-login" element={<FirebaseLogin />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* FBT App Host - PUBLIC ROUTE (no requiere autenticación) */}
            <Route path="grid/:company/:app/*" element={<FBTAppHost />} />
            
            {/* Flow Runner - PUBLIC ROUTE (gateway: supports public/internal flows depending on exposure mode) */}
            <Route path="/flows/:flowKey" element={<FlowRunnerPublicPage />} />
            
            {/* Public Onboarding Wizard - PUBLIC ROUTE (requires API Key, no user auth) */}
            <Route path="/onboarding" element={<PublicOnboardingWizard />} />

            {/* Public Module Page - PUBLIC ROUTE (no auth, serves published Poiesis modules) */}
            {/* Artifact full-screen view - PUBLIC ROUTE (no auth, HMAC-signed token, WEBOT_WIDGET_ARTIFACTS.md PR B) */}
            <Route path="/pub/artifact/:token" element={<ArtifactViewPage />} />
            <Route path="/pub/meeting-screen" element={<MeetingScreenPage />} />

            <Route path="/pub/:publicSlug" element={<PublicModulePage />} />

            {/* ISO Public Marketplace - PUBLIC ROUTE (no auth, lists published ISO products) */}
            <Route path="/marketplace" element={<ISOMarketplacePage />} />

            {/* ISO Product Landing Pages - PUBLIC ROUTE */}
            <Route path="/products/nexor" element={<NexorLandingPage />} />
            <Route path="/products/:recipe_id/start" element={<ISOOnboardingPage />} />

            {/* CofounderBuddy moved to CLU Sites (CLU_SITES_MODULE.md S5): the old public URLs
                send visitors to the site the backend serves at /s/cofounderbuddy. */}
            <Route path="/products/cofounderbuddy" element={<SiteRedirectPage handle="cofounderbuddy" />} />
            <Route path="/m/products/cofounderbuddy" element={<SiteRedirectPage handle="cofounderbuddy" />} />

            {/* Public Invoice Page - PUBLIC ROUTE (no auth, HMAC-signed token, BILLING_COBRANZA_TAREAS.md PASO 6) */}
            <Route path="/i/:token" element={<InvoicePublicPage />} />

            {/* Mobile Routes - Native layout, no iframe */}
            <Route
              path="/m"
              element={
                <ProtectedRoute>
                  <MobileLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/m/clu-ng" replace />} />
              <Route path="clu" element={<ProtectedRoute><MobileCLUPage /></ProtectedRoute>} />
              <Route path="clu-ng" element={<ProtectedRoute><NGChatPage resumeSession /></ProtectedRoute>} />
              <Route path="clu-ng/console/:sessionId" element={<ProtectedRoute requiredRole="admin"><NGConsolePage /></ProtectedRoute>} />
              <Route path="clu-ng/supervisor" element={<ProtectedRoute requiredRole="admin"><NGSupervisorPage /></ProtectedRoute>} />
              <Route path="operator" element={<ProtectedRoute requiredRole="super_user"><OperatorConsolePage /></ProtectedRoute>} />
              <Route path="code-chat" element={<ProtectedRoute><MobileCodeRepoChatPage /></ProtectedRoute>} />
              <Route path="dashboard" element={<ProtectedRoute requiredModule="dashboard"><MobileDashboardPage /></ProtectedRoute>} />
              <Route path="menu" element={<ProtectedRoute><Menu /></ProtectedRoute>} />
              <Route path="notifications" element={<ProtectedRoute requiredRole="admin"><MobileNotificationsPage /></ProtectedRoute>} />
              <Route path="notifications/:notificationId" element={<ProtectedRoute requiredRole="admin"><MobileNotificationsPage /></ProtectedRoute>} />
              <Route path="iot-telemetry" element={<ProtectedRoute requiredRole="admin"><MobileIoTTelemetryPage /></ProtectedRoute>} />
              <Route path="iot-telemetry/devices" element={<ProtectedRoute requiredRole="admin"><MobileIoTDevicesPage /></ProtectedRoute>} />
              <Route path="iot-telemetry/devices/:deviceId" element={<ProtectedRoute requiredRole="admin"><MobileIoTDeviceDetailPage /></ProtectedRoute>} />
              <Route path="iot-telemetry/events" element={<ProtectedRoute requiredRole="admin"><MobileIoTEventsPage /></ProtectedRoute>} />
              <Route path="control-center" element={<ProtectedRoute requiredRole="admin"><MobileControlCenterPage /></ProtectedRoute>} />
              <Route path="clerks-ecosystem" element={<ProtectedRoute requiredRole="admin"><MobileClerksEcosystemPage /></ProtectedRoute>} />
              <Route path="admin/conversations" element={<ProtectedRoute requiredRole="admin"><MobileConversationsPage /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute requiredRole="admin"><Settings /></ProtectedRoute>} />

              {/* Settings sub-routes — Tier 2 mobile responsive */}
              <Route path="settings/general" element={<ProtectedRoute requiredRole="admin"><GeneralSettingsPage /></ProtectedRoute>} />
              {/* PR5 (LLM_COST_TRACKING_REMEDIATION_HANDOFF.md): pricing admin is desktop-only —
                  confirmed with the product owner. Was wholesale-importing the desktop
                  LLMModelsManager component, which §9/TD-027 explicitly forbids. */}
              <Route path="settings/llm-models" element={<ProtectedRoute requiredRole="admin"><MobileRouteFallback /></ProtectedRoute>} />
              <Route path="settings/mcp-gateway" element={<ProtectedRoute requiredRole="admin"><MCPGatewayPage /></ProtectedRoute>} />
              {/* DB Connections is a heavy admin data-grid tool — desktop-only per the same
                  pattern as pricing admin above, not confirmed with product owner as mobile-ready. */}
              <Route path="settings/db-connections" element={<ProtectedRoute requiredRole="admin"><MobileRouteFallback /></ProtectedRoute>} />
              {/* TD-027 mobile cut: review queue + read-only facts explorer are
                  mobile-ready; the catalog editor stays desktop-only (in-page card). */}
              <Route path="settings/capture-targets" element={<ProtectedRoute requiredRole="admin"><MobileCapturePage /></ProtectedRoute>} />
              <Route path="settings/skills" element={<ProtectedRoute requiredRole="admin"><MobileSkillsPage /></ProtectedRoute>} />
              <Route path="settings/scene-symbols" element={<ProtectedRoute requiredRole="admin"><MobileSceneSymbolsPage /></ProtectedRoute>} />
              <Route path="settings/autodemo" element={<ProtectedRoute requiredRole="admin"><AutoDemoSettingsPage /></ProtectedRoute>} />
              <Route path="settings/api-keys" element={<ProtectedRoute requiredRole="super_user"><ApiKeysAdminPage /></ProtectedRoute>} />
              <Route path="settings/api-keys/my-keys" element={<ProtectedRoute requiredRole="admin"><MyApiKeysPage /></ProtectedRoute>} />
              <Route path="settings/credentials-admin" element={<ProtectedRoute requiredRole="super_user"><CredentialsAdmin /></ProtectedRoute>} />
              <Route path="settings/clu-admin" element={<ProtectedRoute requiredRole="admin"><CLUAdminPage /></ProtectedRoute>} />
              <Route path="settings/connectors-library" element={<ProtectedRoute requiredRole="admin"><ConnectorsLibrary /></ProtectedRoute>} />
              <Route path="settings/connector-catalog" element={<ProtectedRoute requiredRole="admin"><ConnectorCatalogPage /></ProtectedRoute>} />
              <Route path="settings/tools-audit" element={<ProtectedRoute requiredRole="admin"><ToolsAudit /></ProtectedRoute>} />
              <Route path="settings/tml-admin" element={<ProtectedRoute requiredRole="admin"><TMLAdmin /></ProtectedRoute>} />
              <Route path="settings/bigquery-console" element={<ProtectedRoute requiredRole="admin"><BigQueryConsole /></ProtectedRoute>} />
              <Route path="settings/sqlconsoleserver" element={<ProtectedRoute requiredRole="admin"><SQLConsoleServer /></ProtectedRoute>} />
              <Route path="settings/sql-queries-vault" element={<ProtectedRoute requiredRole="admin"><SQLQueriesVault /></ProtectedRoute>} />
              <Route path="settings/flow-designer" element={<ProtectedRoute requiredRole="admin"><FlowDesignerAdminPage /></ProtectedRoute>} />
              <Route path="settings/smartagents" element={<ProtectedRoute requiredRole="admin"><SmartAgentDesigner /></ProtectedRoute>} />
              <Route path="settings/budget-planning" element={<ProtectedRoute requiredRole="admin"><BudgetPlanningPage /></ProtectedRoute>} />
              <Route path="settings/quantum-readiness" element={<ProtectedRoute requiredRole="admin"><QuantumReadinessPage /></ProtectedRoute>} />
              <Route path="settings/approval-workflows" element={<ProtectedRoute requiredRole="admin"><ApprovalWorkflowsPage /></ProtectedRoute>} />
              <Route path="settings/analyst-studio" element={<ProtectedRoute requiredRole="admin"><MobileAnalystStudioPage /></ProtectedRoute>} />
              <Route path="settings/iot-telemetry" element={<ProtectedRoute requiredRole="admin"><IoTTelemetryPage /></ProtectedRoute>} />
              <Route path="settings/iso" element={<ProtectedRoute requiredRole="admin"><MobileISOPage /></ProtectedRoute>} />
              <Route path="settings/semantic-index" element={<ProtectedRoute requiredRole="admin"><SemanticIndexPage /></ProtectedRoute>} />
              <Route path="settings/homologation" element={<ProtectedRoute requiredRole="admin"><MobileHomologationPage /></ProtectedRoute>} />
              <Route path="settings/my-memory" element={<MyMemoryPage />} />
              <Route path="settings/finance/pipeline" element={<ProtectedRoute requiredRole="admin"><MobilePipelinePage /></ProtectedRoute>} />
              <Route path="settings/finance" element={<ProtectedRoute requiredRole="admin"><FinancialIntelligencePage /></ProtectedRoute>} />
              <Route path="settings/compliance" element={<ProtectedRoute requiredRole="admin"><CompliancePage /></ProtectedRoute>} />
              <Route path="settings/onboarding" element={<ProtectedRoute requiredRole="admin"><OnboardingAdminPage /></ProtectedRoute>} />
              <Route path="settings/clu-rag-training" element={<ProtectedRoute requiredRole="super_user"><CluRagTrainingPage /></ProtectedRoute>} />
              <Route path="settings/workers-admin" element={<ProtectedRoute requiredRole="admin"><WorkersAdminPage /></ProtectedRoute>} />
              <Route path="settings/system-health" element={<ProtectedRoute requiredRole="admin"><SystemHealthNocPage /></ProtectedRoute>} />
              <Route path="settings/a2a-message-bus" element={<ProtectedRoute requiredRole="admin"><A2AMessageBusPage /></ProtectedRoute>} />
              <Route path="settings/navigator-admin" element={<ProtectedRoute requiredRole="admin"><MobileNavigatorPage /></ProtectedRoute>} />
              <Route path="settings/redis" element={<ProtectedRoute requiredRole="admin"><RedisAdminPage /></ProtectedRoute>} />
              <Route path="settings/sentinel" element={<ProtectedRoute requiredRole="admin"><SentinelAdminPage /></ProtectedRoute>} />
              <Route path="settings/sentinel/:agentId" element={<ProtectedRoute requiredRole="admin"><SentinelAdminPage /></ProtectedRoute>} />
              <Route path="settings/llm-costs" element={<ProtectedRoute requiredRole="admin"><LLMCostDashboard /></ProtectedRoute>} />
              <Route path="settings/rls" element={<ProtectedRoute requiredRole="admin"><RLSManagement /></ProtectedRoute>} />

              {/* Other top-level routes accessible from mobile */}
              <Route path="users" element={<ProtectedRoute requiredRole="admin"><UserManagement /></ProtectedRoute>} />
              <Route path="organizations" element={<ProtectedRoute requiredRole="super_user"><OrganizationManagement /></ProtectedRoute>} />
              <Route path="billing" element={<ProtectedRoute requiredRole="admin"><MobileBillingPage /></ProtectedRoute>} />
              <Route path="collections" element={<ProtectedRoute requiredRole="super_user"><MobileCollectionsPage /></ProtectedRoute>} />
              <Route path="quantum-readiness" element={<ProtectedRoute requiredRole="admin"><MobileQuantumReadinessPage /></ProtectedRoute>} />
              <Route path="profile" element={<ProtectedRoute><MobileProfilePage /></ProtectedRoute>} />
              <Route path="settings/my-workspace" element={<ProtectedRoute><MobileWorkspacePage /></ProtectedRoute>} />
              <Route path="llm-costs" element={<ProtectedRoute requiredRole="admin"><LLMCostDashboard /></ProtectedRoute>} />
              <Route path="admin/soa" element={<ProtectedRoute requiredRole="admin"><SOADashboard /></ProtectedRoute>} />
              <Route path="admin/smartagents" element={<ProtectedRoute requiredRole="admin"><SmartAgentDesigner /></ProtectedRoute>} />
              <Route path="galaxia" element={<ProtectedRoute><MobileGalaxiaPage /></ProtectedRoute>} />
              <Route path="menu" element={<ProtectedRoute><Menu /></ProtectedRoute>} />
              <Route path="navigator-admin" element={<ProtectedRoute requiredRole="admin"><MobileNavigatorPage /></ProtectedRoute>} />
              <Route path="jobs" element={<ProtectedRoute><MobileJobsPage /></ProtectedRoute>} />
              <Route path="settings/a2a-message-bus" element={<ProtectedRoute requiredRole="admin"><MobileA2AMessageBusPage /></ProtectedRoute>} />
              <Route path="autodemo" element={<ProtectedRoute><MobileAutoDemoPage /></ProtectedRoute>} />
              <Route path="my-wallet" element={<ProtectedRoute><MobileWalletPage /></ProtectedRoute>} />
              <Route path="settings/smartagents" element={<ProtectedRoute requiredRole="admin"><MobileSmartAgentsPage /></ProtectedRoute>} />
              <Route path="settings/cie-campaigns" element={<ProtectedRoute requiredRole="admin"><MobileCampaignsPage /></ProtectedRoute>} />
              <Route path="settings/segmentation-admin" element={<ProtectedRoute requiredRole="admin"><MobileSegmentationPage /></ProtectedRoute>} />
              <Route path="sentinel-360" element={<ProtectedRoute requiredRole="admin"><MobileSentinel360Page /></ProtectedRoute>} />
              <Route path="sentinel-360/:agentId" element={<ProtectedRoute requiredRole="admin"><MobileSentinel360Page /></ProtectedRoute>} />
              <Route path="analytics" element={<ProtectedRoute requiredRole="admin"><MobileAnalyticsPage /></ProtectedRoute>} />
              {/* CLU Studio is single-column mobile-first by design — same component */}
              <Route path="studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
              <Route path="process-studio" element={<ProtectedRoute requiredRole="admin"><MobileProcessStudioPage /></ProtectedRoute>} />
              <Route path="process-studio/builder/:processId" element={<ProtectedRoute requiredRole="admin"><MobileProcessBuilderPage /></ProtectedRoute>} />
              <Route path="process-studio/:processId" element={<ProtectedRoute requiredRole="admin"><MobileProcessDetailPage /></ProtectedRoute>} />

              <Route path="settings/aa-setup/*" element={<ProtectedRoute requiredRole="admin"><MobileAASetupStubPage /></ProtectedRoute>} />
              <Route path="settings/hyper-core/knowledge-sources" element={<ProtectedRoute requiredRole="admin"><MobileKnowledgeSourcesPage /></ProtectedRoute>} />
              <Route path="settings/video-pipeline" element={<ProtectedRoute requiredRole="admin"><MobileVideoPipelinePage /></ProtectedRoute>} />
              <Route path="settings/video-ingestion" element={<ProtectedRoute requiredRole="admin"><MobileVideoIngestionPage /></ProtectedRoute>} />
              <Route path="settings/semantic-index" element={<ProtectedRoute requiredRole="admin"><MobileSemanticIndexPage /></ProtectedRoute>} />
              <Route path="settings/tool-workbench" element={<ProtectedRoute requiredRole="admin"><MobileToolWorkbenchPage /></ProtectedRoute>} />
              <Route path="drive-monitor" element={<ProtectedRoute requiredRole="admin"><MobileDriveMonitorStatus /></ProtectedRoute>} />
              <Route path="settings/banking" element={<ProtectedRoute requiredRole="admin"><MobileBankingPage /></ProtectedRoute>} />
              <Route path="settings/file-manager" element={<ProtectedRoute requiredRole="super_user"><MobileGCSFileManagerPage /></ProtectedRoute>} />
              {/* The review screen is a reading task, not a data-entry one — the
                  desktop page is already single-column and touch-sized, so it
                  serves /m/* directly rather than through a stripped-down twin
                  that would have to hide the very slide pictures you review. */}
              <Route path="settings/output-artifacts" element={<ProtectedRoute requiredRole="admin"><ArtifactTemplatesPage /></ProtectedRoute>} />
              <Route path="drive" element={<ProtectedRoute><MobileUserDrivePage /></ProtectedRoute>} />
              <Route path="drive/:agentId" element={<ProtectedRoute><MobileUserDrivePage /></ProtectedRoute>} />
              <Route path="settings/voice-campaigns" element={<ProtectedRoute requiredRole="admin"><MobileVoiceCallsPage /></ProtectedRoute>} />
              <Route path="settings/council" element={<ProtectedRoute requiredRole="admin"><MobileCouncilPage /></ProtectedRoute>} />
              <Route path="shiftsense" element={<ProtectedRoute requiredRole="admin"><MobileShiftSensePage /></ProtectedRoute>} />
              <Route path="shiftsense/events" element={<ProtectedRoute requiredRole="admin"><MobileShiftSenseEventsPage /></ProtectedRoute>} />
              <Route path="shiftsense/staff" element={<ProtectedRoute requiredRole="admin"><MobileShiftSenseStaffPage /></ProtectedRoute>} />
              <Route path="x/commerce" element={<ProtectedRoute requiredRole="admin" requiredModule="commerce"><MobileCommercePage /></ProtectedRoute>} />
              <Route path="data-intake" element={<ProtectedRoute requiredRole="admin"><MobileDataIntakePage /></ProtectedRoute>} />
              <Route path="settings/reference-values" element={<ProtectedRoute requiredRole="admin"><MobileReferenceValuesPage /></ProtectedRoute>} />
              <Route path="settings/samples" element={<ProtectedRoute requiredRole="admin"><MobileSamplesPage /></ProtectedRoute>} />
              <Route path="settings/quoting" element={<ProtectedRoute requiredRole="admin"><MobileQuotingPage /></ProtectedRoute>} />
              <Route path="settings/persona-profile" element={<ProtectedRoute requiredRole="admin"><MobilePersonaProfilePage /></ProtectedRoute>} />
              <Route path="settings/sites" element={<ProtectedRoute requiredRole="admin"><MobileSitesPage /></ProtectedRoute>} />
              <Route path="settings/digital-assets" element={<ProtectedRoute requiredRole="admin"><MobileDigitalAssetsPage /></ProtectedRoute>} />
              <Route path="settings/digital-mentor" element={<ProtectedRoute requiredRole="admin"><MobileDigitalMentorPage /></ProtectedRoute>} />
              <Route path="settings/meeting-agent" element={<ProtectedRoute><MobileMeetingAgentPage /></ProtectedRoute>} />
              <Route path="poiesis-builder" element={<ProtectedRoute requiredRole="admin"><MobilePoiesisPage /></ProtectedRoute>} />
              <Route path="settings/poiesis-builder" element={<ProtectedRoute requiredRole="admin"><MobilePoiesisPage /></ProtectedRoute>} />
              <Route path="settings/sap-agent" element={<ProtectedRoute requiredRole="admin"><MobileLocalRuntimePage /></ProtectedRoute>} />
              <Route path="settings/local-runtime" element={<ProtectedRoute requiredRole="admin"><MobileLocalRuntimePage /></ProtectedRoute>} />
              <Route path="settings/api-keys/services" element={<ProtectedRoute requiredRole="super_user"><MobileServiceKeysPage /></ProtectedRoute>} />
              <Route path="settings/document-pipeline/runs/:runId" element={<ProtectedRoute requiredRole="admin"><MobileDocumentPipelineRunPage /></ProtectedRoute>} />
              <Route path="settings/channels-catalog" element={<ProtectedRoute requiredRole="admin"><MobileChannelsPage /></ProtectedRoute>} />
              <Route path="admin/deploys" element={<ProtectedRoute requiredRole="super_user"><MobileDeploysPage /></ProtectedRoute>} />
              <Route path="settings/llm-visibility" element={<ProtectedRoute requiredRole="admin"><MobileLlmVisibilityPage /></ProtectedRoute>} />
              <Route path="training" element={<ProtectedRoute requiredRole="admin"><MobileTrainingPage /></ProtectedRoute>} />
              <Route path="settings/helpdesk-tickets" element={<ProtectedRoute requiredRole="admin"><MobileHelpdeskTicketsPage /></ProtectedRoute>} />
              <Route path="settings/ml-management" element={<ProtectedRoute requiredRole="admin"><MobileMLStudioPage /></ProtectedRoute>} />
              <Route path="live-assets" element={<ProtectedRoute><MobileLiveAssetsPage /></ProtectedRoute>} />
              <Route path="settings/*" element={<ProtectedRoute requiredRole="admin"><MobileRouteFallback /></ProtectedRoute>} />
              <Route path="*" element={<MobileRouteFallback />} />
            </Route>

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  {localStorage.getItem('clu_shell_v2') !== '0' ? <LayoutV2 /> : <Layout />}
                </ProtectedRoute>
              }
            >
              {/* Default route - Redirects based on user role: super_user → /clerks-ecosystem, others → /menu */}
              <Route index element={
                <ProtectedRoute>
                  <DefaultRedirect />
                </ProtectedRoute>
              } />

              {/* Main Dashboard */}
              <Route path="dashboard" element={
                <ProtectedRoute requiredModule="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              } />

              {/* ISO Shell — instance mini-dashboard (authenticated) */}
              <Route path="shell/:instance_id" element={
                <ProtectedRoute>
                  <ShellPage />
                </ProtectedRoute>
              } />
              
              {/* Menu Page - Shows all available modules */}
              <Route path="menu" element={
                <ProtectedRoute>
                  <Menu />
                </ProtectedRoute>
              } />
              
              {/* Database Tables Management - Moved inside protected Layout */}
              <Route 
                path="db-connections/:connectionId/tables" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <TablesPage />
                  </ProtectedRoute>
                } 
              />

              {/* Redirect from old chat to new Data Assistant */}
              <Route path="chat" element={<Navigate to="/chat-v5" replace />} />
              <Route path="chat-v2" element={<Navigate to="/chat-v5" replace />} />

              {/* SQL Assistant V5 */}
              <Route path="chat-v5" element={
                <ProtectedRoute requiredModule="data_assistant">
                  <AgentChatV5 />
                </ProtectedRoute>
              } />

              {/* Unified Assistant */}
              <Route path="assistant" element={
                <ProtectedRoute requiredModule="data_assistant">
                  <IntegratedAssistant />
                </ProtectedRoute>
              } />

              {/* SQL Assistant V5 Test Page - NEW (Super User Only) */}
              <Route path="chat-v5-test" element={
                <ProtectedRoute requiredRole="super_user" requiredModule="data_assistant">
                  <TestAgentChatV5 />
                </ProtectedRoute>
              } />

              {/* SmartDataHelper - Dynamic Data Enhanced (Super User Only) */}
              <Route path="chat-v6" element={
                <ProtectedRoute requiredRole="user" requiredModule="knowledge_base">
                  <SmartDataHelper />
                </ProtectedRoute>
              } />

              {/* Face Verify Home - Main entry point */}
              <Route path="face-verify-home/:agentId?" element={
                <ProtectedRoute requiredRole="user">
                  <FaceVerifyHome />
                </ProtectedRoute>
              } />

              {/* Face Verify Admin - Biometric Identity Agent */}
              <Route path="face-verify/:agentId" element={
                <ProtectedRoute requiredRole="admin">
                  <FaceVerifyAdmin />
                </ProtectedRoute>
              } />

              {/* Face Enrollment - Simple enrollment flow */}
              <Route path="face-enroll/:agentId" element={
                <ProtectedRoute requiredRole="user">
                  <FaceEnroll />
                </ProtectedRoute>
              } />

              {/* Face Verify - Simple verification flow */}
              <Route path="face-check/:agentId" element={
                <ProtectedRoute requiredRole="user">
                  <FaceVerify />
                </ProtectedRoute>
              } />

              {/* Face Review Queue - HIL review interface */}
              <Route path="face-review/:agentId" element={
                <ProtectedRoute requiredRole="admin">
                  <FaceReviewQueue />
                </ProtectedRoute>
              } />

              {/* SmartDataHelper (Alternative route) */}
              <Route path="smart-data-helper" element={
                <ProtectedRoute requiredRole="user" requiredModule="knowledge_base">
                  <SmartDataHelper />
                </ProtectedRoute>
              } />

              {/* Analytics Chat V1 - Conversational Analytics */}
              <Route path="analytics-chat-v1" element={
                <ProtectedRoute requiredModule="data_assistant">
                  <AnalyticsChatV1 />
                </ProtectedRoute>
              } />

              {/* Usage Analytics Dashboard */}
              <Route path="analytics" element={
                <ProtectedRoute requiredRole="admin" requiredModule="usage_analytics">
                  <AnalyticsPage />
                </ProtectedRoute>
              } />

              {/* BELLA VIBE - Voice Interface */}
              <Route path="bella-vibe" element={
                <ProtectedRoute requiredModule="bella_vibe">
                  <BellaVibePage />
                </ProtectedRoute>
              } />

              {/* BELLA VIBE V3 - JARVIS-like Voice Interface */}
              <Route path="bella-vibe-v3" element={
                <ProtectedRoute requiredModule="bella_vibe">
                  <BellaVibeV3Page />
                </ProtectedRoute>
              } />



              {/* User Management - Admin only */}
              <Route
                path="users"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="user_management">
                    <UserManagement />
                  </ProtectedRoute>
                }
              />

              {/* User Detail */}
              <Route
                path="users/:email"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="user_management">
                    <UserDetail />
                  </ProtectedRoute>
                }
              />

              {/* User Modules Management */}
              <Route
                path="users/:email/modules"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="user_management">
                    <UserModules />
                  </ProtectedRoute>
                }
              />

              {/* User Clerks Management */}
              <Route
                path="users/:email/agents"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="user_management">
                    <UserAgents />
                  </ProtectedRoute>
                }
              />

              {/* User Organizations Management */}
              <Route
                path="users/:email/organizations"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="user_management">
                    <UserOrganizations />
                  </ProtectedRoute>
                }
              />

              {/* Organization Management - Super User only */}
              <Route
                path="organizations"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="organization_management">
                    <OrganizationManagement />
                  </ProtectedRoute>
                }
              />

              {/* Organization Modules Management */}
              <Route
                path="organizations/:id/modules"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="organization_management">
                    <OrganizationModules />
                  </ProtectedRoute>
                }
              />

              {/* Organization Users Management */}
              <Route
                path="organizations/:id/users"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="organization_management">
                    <OrganizationUsers />
                  </ProtectedRoute>
                }
              />

              {/* Organization Clerks Management */}
              <Route
                path="organizations/:id/clerks"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="organization_management">
                    <OrganizationClerksPage />
                  </ProtectedRoute>
                }
              />

              {/* Organization Profile */}
              <Route
                path="organizations/:id/profile"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="organization_management">
                    <OrganizationProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* Agent Management - Admin only */}
              <Route
                path="agents"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="agents_admin">
                    <AgentManagement />
                  </ProtectedRoute>
                }
              />

              {/* Clerks 2.0 Ecosystem - Admin only */}
              <Route
                path="clerks-ecosystem"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="clerks_ecosystem_">
                    <ClerksEcosystemPage />
                  </ProtectedRoute>
                }
              />

              {/* Clerk Chain Health - Provisioning audit */}
              <Route
                path="clerks-chain"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ClerksChainPage />
                  </ProtectedRoute>
                }
              />

              {/* Knowledge Base Management */}
              <Route
                path="ingestion"
                element={
                  <ProtectedRoute requiredRole="admin" requiredModule="knowledge_base">
                    <KnowledgeIngestion />
                  </ProtectedRoute>
                }
              />

              {/* Semantic Models - Settings Page (Super User) */}
              <Route
                path="settings/semantic-models/:tool?"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="data_assistant">
                    <SemanticModelsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/semantic-models"
                element={
                  <ProtectedRoute requiredRole="super_user" requiredModule="data_assistant">
                    <SemanticModelsPage />
                  </ProtectedRoute>
                }
              />

              {/* MDG Admin Console (Admin) */}
              <Route
                path="settings/mdg"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <MDGAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/castor"
                element={
                  <ProtectedRoute requiredRole="super_user">
                    <CastorPage />
                  </ProtectedRoute>
                }
              />
              {/* Data Transformation - like Rules Evaluation (home → configs list → view/edit) */}
              <Route path="data-transformation" element={
                <ProtectedRoute requiredRole="admin">
                  <DataTransformationHome />
                </ProtectedRoute>
              } />
              <Route path="data-transformation/configs" element={
                <ProtectedRoute requiredRole="admin">
                  <TransformConfigsList />
                </ProtectedRoute>
              } />
              <Route path="data-transformation/configs/new" element={
                <ProtectedRoute requiredRole="admin">
                  <TransformConfigCreate />
                </ProtectedRoute>
              } />
              <Route path="data-transformation/configs/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <TransformConfigView />
                </ProtectedRoute>
              } />
              <Route path="data-transformation/configs/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <TransformConfigEdit />
                </ProtectedRoute>
              } />
              {/* Legacy: redirect old settings path to new module */}
              <Route path="settings/transform-configs" element={
                <ProtectedRoute requiredRole="admin">
                  <TransformConfigsPage />
                </ProtectedRoute>
              } />

              {/* CLU Sentinel - Strategic Competitive Intelligence (Admin) */}
              <Route
                path="settings/sentinel/:agentId?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SentinelAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/sentinel"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SentinelAdminPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Sentinel Ops - Internal Organizational Intelligence */}
              <Route
                path="settings/sentinel-ops/:agentId?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SentinelOpsAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/sentinel-ops"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SentinelOpsAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* CLU Sentinel 360° Dashboard - Complete strategic intelligence view */}
              <Route
                path="sentinel-360/:agentId?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Sentinel360Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sentinel-360"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Sentinel360Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* SDR Agent - Independent decoupled page */}
              <Route
                path="settings/sdr-agent/:agentId?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SDRAgentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/sdr-agent"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SDRAgentPage />
                  </ProtectedRoute>
                }
              />

              {/* PMM Playbooks - Operational Narrative Weapons (Admin) */}
              <Route
                path="settings/pmm-playbooks/:agentId?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PMMPlaybooksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="settings/pmm-playbooks"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PMMPlaybooksPage />
                  </ProtectedRoute>
                }
              />

              {/* Legacy routes - redirect to settings */}
              <Route
                path="semantic-domains/:agentId"
                element={
                  <Navigate to="/settings/semantic-models/semantic-domain-builder" replace />
                }
              />
              <Route
                path="semantic-domains"
                element={
                  <Navigate to="/settings/semantic-models/semantic-domain-builder" replace />
                }
              />
              <Route
                path="data-copilot/:agentId"
                element={
                  <Navigate to="/settings/semantic-models/data-copilot-ask" replace />
                }
              />
              <Route
                path="data-copilot"
                element={
                  <Navigate to="/settings/semantic-models/data-copilot-ask" replace />
                }
              />

              {/* GRID Report Builder */}
              <Route
                path="report-builder"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <ReportBuilderDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Survey Management - Legacy route redirects to settings */}
              <Route
                path="survey/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Navigate to="/settings/survey" replace />
                  </ProtectedRoute>
                }
              />
              
              {/* Survey Management - Settings route */}
              <Route
                path="settings/survey"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SurveyAdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Flow Designer (Admin) */}
              <Route
                path="settings/flow-designer"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <FlowDesignerAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Monitoring was replaced by Live Assets (TD-271) */}
              <Route path="monitoring" element={<Navigate to="/live-assets" replace />} />

              {/* Profiling Center */}
              <Route path="profiling" element={
                <ProtectedRoute requiredRole="admin">
                  <ProfilingAdmin />
                </ProtectedRoute>
              } />
              <Route path="settings/profiling" element={
                <ProtectedRoute requiredRole="admin">
                  <ProfilingAdmin />
                </ProtectedRoute>
              } />
              <Route path="profiling/segmentation" element={
                <ProtectedRoute requiredRole="admin">
                  <SegmentationAdmin />
                </ProtectedRoute>
              } />
              <Route path="profiling/cie-campaigns" element={
                <ProtectedRoute requiredRole="admin">
                  <CIECampaignManager />
                </ProtectedRoute>
              } />
              <Route path="profiling/hyper-core" element={
                <ProtectedRoute requiredRole="admin">
                  <HyperCoreAdmin />
                </ProtectedRoute>
              } />
              <Route path="profiling/yori" element={
                <ProtectedRoute requiredRole="admin">
                  <YoriAdminPage />
                </ProtectedRoute>
              } />

              {/* Control Center */}
              <Route path="control-center" element={
                <ProtectedRoute requiredRole="admin">
                  <ControlCenter />
                </ProtectedRoute>
              } />

              {/* BigQuery Console */}
              <Route path="settings/bigquery-console" element={
                <ProtectedRoute requiredRole="admin">
                  <BigQueryConsole />
                </ProtectedRoute>
              } />

              {/* Credentials Admin (Super User) */}
              <Route path="settings/credentials-admin" element={
                <ProtectedRoute requiredRole="super_user">
                  <CredentialsAdmin />
                </ProtectedRoute>
              } />

              {/* API Keys Management */}
              <Route path="settings/api-keys" element={
                <ProtectedRoute requiredRole="super_user">
                  <ApiKeysAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/api-keys/my-keys" element={
                <ProtectedRoute requiredRole="admin">
                  <MyApiKeysPage />
                </ProtectedRoute>
              } />
              <Route path="settings/onboarding" element={
                <ProtectedRoute requiredRole="super_user">
                  <OnboardingAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/clu-rag-training" element={
                <ProtectedRoute requiredRole="super_user">
                  <CluRagTrainingPage />
                </ProtectedRoute>
              } />

              {/* LLM Cost Dashboard */}
              <Route path="llm-costs" element={
                <ProtectedRoute requiredRole="admin">
                  <LLMCostDashboard />
                </ProtectedRoute>
              } />

              {/* Navigator Admin */}
              <Route path="navigator-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <NavigatorAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/navigator-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <NavigatorAdminPage />
                </ProtectedRoute>
              } />
              {/* Navigator Performance Monitor (legacy route) */}
              <Route path="navigator-performance" element={
                <ProtectedRoute requiredRole="admin">
                  <NavigatorPerformanceMonitor />
                </ProtectedRoute>
              } />
              <Route path="settings/navigator-performance" element={
                <ProtectedRoute requiredRole="admin">
                  <NavigatorPerformanceMonitor />
                </ProtectedRoute>
              } />

              {/* Dynamic Dashboard */}
              <Route path="dynamic-dashboard" element={
                <ProtectedRoute requiredModule="dynamic_dashboard">
                  <DynamicDashboard />
                </ProtectedRoute>
              } />

              {/* CLU Interface - Public for all authenticated users */}
              <Route path="clu" element={
                <ProtectedRoute>
                  <CLUPage />
                </ProtectedRoute>
              } />

              {/* Live Assets — persisted, refreshable views */}
              <Route path="live-assets" element={
                <ProtectedRoute>
                  <LiveAssetsPage />
                </ProtectedRoute>
              } />

              {/* CLU Chat NG — loop-first chat (experimental, backend flag-gated) */}
              <Route path="clu-ng" element={
                <ProtectedRoute>
                  <NGChatPage resumeSession />
                </ProtectedRoute>
              } />

              {/* Session Room agent console — an admin supervising/joining
                  someone else's CLU NG session. §127 */}
              <Route path="clu-ng/console/:sessionId" element={
                <ProtectedRoute requiredRole="admin">
                  <NGConsolePage />
                </ProtectedRoute>
              } />

              {/* Session Room supervisor view — one room observing N. §130 */}
              <Route path="clu-ng/supervisor" element={
                <ProtectedRoute requiredRole="admin">
                  <NGSupervisorPage />
                </ProtectedRoute>
              } />

              {/* CLU Operator console — super_user view over CLU Operator's own
                  home sessions, one per tenant. Phase 2a,
                  backend/docs/CLU_OPERATOR_DESIGN.md. */}
              <Route path="operator" element={
                <ProtectedRoute requiredRole="super_user">
                  <OperatorConsolePage />
                </ProtectedRoute>
              } />

              {/* Code Repo Chat - evidence-gated repo chat for all authenticated users */}
              <Route path="code-chat" element={
                <ProtectedRoute>
                  <CodeRepoChatPage />
                </ProtectedRoute>
              } />

              {/* Ecosystem Explorer - Interactive graph of agent connectors */}
              <Route path="ecosystem-explorer" element={
                <ProtectedRoute>
                  <EcosystemExplorer />
                </ProtectedRoute>
              } />

              {/* ⚠️ POE routes eliminadas - Ver POE_REMOVAL_GUIDE.md */}

              {/* SHPE (Simple Health Plan Engine) - Main Entry Point */}
              <Route path="shpe" element={
                <ProtectedRoute>
                  <SHPEHome />
                </ProtectedRoute>
              } />

              {/* Product Recomendator (Generic Product Comparator Agent) - Moved to Settings */}
              <Route path="settings/product-recomendator" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ProductRecomendatorDashboard />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/config" element={
                <ProtectedRoute requiredModule="product_recomendator" requiredRole="admin">
                  <ProductTypeConfigManager />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/products/:productType/:productCode/edit" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ProductEditor />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/products/:productType/:productCode" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ProductDetailView />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/products" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ProductManager />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/compare" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ProductComparisonView />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/recommendations" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <RecommendationBuilder />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/roi" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <ROICalculator />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/documents/manage" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <DocumentManagement />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/documents" element={
                <ProtectedRoute requiredModule="product_recomendator">
                  <DocumentProcessingView />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/analytics" element={
                <ProtectedRoute requiredModule="product_recomendator" requiredRole="admin">
                  <AnalyticsDashboard />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/a2a-monitor" element={
                <ProtectedRoute requiredModule="product_recomendator" requiredRole="admin">
                  <A2AMessageMonitor />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/a2a-tester" element={
                <ProtectedRoute requiredModule="product_recomendator" requiredRole="admin">
                  <A2AMessageTester />
                </ProtectedRoute>
              } />
              <Route path="settings/product-recomendator/a2a-config" element={
                <ProtectedRoute requiredModule="product_recomendator" requiredRole="admin">
                  <A2AConfigurationPanel />
                </ProtectedRoute>
              } />

              {/* Graph Memory — unified graph admin (health + bulk loader) */}
              <Route path="settings/graph-memory" element={
                <ProtectedRoute requiredRole="admin">
                  <GraphMemoryAdminPage />
                </ProtectedRoute>
              } />

              {/* FBT App Host moved to public routes above */}

              {/* Autonomous Agents */}
              <Route path="aab/plans" element={
                <ProtectedRoute>
                  <AgentActionPlansList />
                </ProtectedRoute>
              } />
              <Route path="aab/plans/new" element={
                <ProtectedRoute>
                  <AgentActionPlanBuilder />
                </ProtectedRoute>
              } />
              <Route path="aab/plans/:id" element={
                <ProtectedRoute>
                  <AgentActionPlanDetail />
                </ProtectedRoute>
              } />

              {/* CIE Campaign Intelligence Engine - Internal Module */}
              <Route path="cie-campaigns" element={
                <ProtectedRoute requiredRole="admin">
                  <CIECampaignManager />
                </ProtectedRoute>
              } />

              {/* Job Center */}
              <Route path="jobs" element={
                <ProtectedRoute>
                  <MobileJobsPage />
                </ProtectedRoute>
              } />

              {/* Navigator / RPIA */}
              <Route path="navigator-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <MobileNavigatorPage />
                </ProtectedRoute>
              } />

              {/* User Profile */}
              <Route path="profile" element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              } />

              {/* My Workspace */}
              <Route path="settings/my-workspace" element={
                <ProtectedRoute>
                  <MyWorkspacePage />
                </ProtectedRoute>
              } />

              {/* My Wallet */}
              <Route path="my-wallet" element={
                <ProtectedRoute>
                  <UserCredentialWallet />
                </ProtectedRoute>
              } />

              {/* CLU Plan Subscription */}
              <Route path="subscribe" element={<SubscribePage />} />
              <Route path="subscribe/success" element={<SubscribeSuccessPage />} />

              {/* Quorra Commerce Admin — the store CONFIGURATOR lives inside the
                  CLU shell (sidebar, header, Command Palette). Only the public
                  storefront (/store/:slug) is outside it. */}
              <Route path="x/commerce" element={
                <ProtectedRoute requiredRole="admin" requiredModule="commerce">
                  <CommerceDashboardPage />
                </ProtectedRoute>
              } />

              {/* MCP Device Authorization (RFC 8628) */}
              <Route path="x/oauth/device" element={<OAuthDevicePage />} />

              {/* MCP OAuth 2.0 Authorization Code flow consent page */}
              <Route path="x/oauth/authorize" element={<OAuthAuthorizePage />} />

              {/* Notifications Center */}
              <Route path="notifications" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />
              <Route path="notifications/:notificationId" element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              } />

              {/* SmartAgent Deployment Wizard (Admin) - Must be before generic settings route */}
              <Route path="settings/smartagent-wizard" element={
                <ProtectedRoute requiredRole="admin">
                  <SmartAgentWizardPage />
                </ProtectedRoute>
              } />

              {/* Settings - Con rutas anidadas */}
              <Route path="settings" element={
                <ProtectedRoute requiredRole="admin">
                  <Settings />
                </ProtectedRoute>
              } />
              
              {/* Settings - General */}
              <Route path="settings/general" element={
                <ProtectedRoute requiredRole="super_user">
                  <GeneralSettingsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Integrations */}
              <Route path="settings/integrations" element={
                <ProtectedRoute requiredRole="super_user">
                  <SlackIntegration />
                </ProtectedRoute>
              } />

              {/* Settings - Email Ops */}
              <Route path="settings/email-ops" element={
                <ProtectedRoute requiredRole="admin">
                  <EmailOpsPage />
                </ProtectedRoute>
              } />

              {/* Settings - CodeRepo (Admin & Super User) */}
              <Route path="settings/code-repo" element={
                <ProtectedRoute requiredRole="admin">
                  <CodeRepoAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Yori (Admin) */}
              <Route path="settings/yori" element={
                <ProtectedRoute requiredRole="admin">
                  <YoriAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Shortcuts */}
              <Route path="settings/shpe" element={
                <ProtectedRoute>
                  <Navigate to="/shpe" replace />
                </ProtectedRoute>
              } />

              <Route path="settings/cie-campaigns" element={
                <ProtectedRoute requiredRole="super_user">
                  <CIECampaignManager />
                </ProtectedRoute>
              } />
              <Route path="settings/segmentation-admin" element={
                <ProtectedRoute requiredRole="super_user">
                  <SegmentationAdmin />
                </ProtectedRoute>
              } />
              <Route path="settings/ml-management" element={
                <ProtectedRoute requiredRole="admin">
                  <MLStudioPage />
                </ProtectedRoute>
              } />

              {/* Settings - Providers Admin (Admin or Super User) */}
              <Route path="settings/providers-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <ProvidersAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Vision Admin (Admin) */}
              <Route path="settings/vision-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <VisionAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - Pattern Engine Admin */}
              <Route path="settings/patterns" element={
                <ProtectedRoute requiredRole="admin">
                  <PatternsAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/patterns/new" element={
                <ProtectedRoute requiredRole="super_user">
                  <PatternEditor />
                </ProtectedRoute>
              } />
              <Route path="settings/patterns/:patternId" element={
                <ProtectedRoute requiredRole="super_user">
                  <PatternEditor />
                </ProtectedRoute>
              } />
              <Route path="settings/patterns/playground" element={
                <ProtectedRoute requiredRole="admin">
                  <PatternPlayground />
                </ProtectedRoute>
              } />
              <Route path="settings/patterns/events" element={
                <ProtectedRoute requiredRole="admin">
                  <PatternEventsExplorer />
                </ProtectedRoute>
              } />
              <Route path="settings/patterns/streams-monitor" element={
                <ProtectedRoute requiredRole="admin">
                  <StreamsMonitor />
                </ProtectedRoute>
              } />

              {/* Settings - Channels capsule (Admin or Super User) */}
              <Route path="settings/channels" element={
                <ProtectedRoute requiredRole="admin">
                  <ChannelsAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - Widget Bots (Admin) */}
              <Route path="settings/widget-bots" element={
                <ProtectedRoute requiredRole="admin">
                  <WidgetBotsAdminPage />
                </ProtectedRoute>
              } />
              <Route path="admin/widget-bots/:botKey" element={
                <ProtectedRoute requiredRole="admin">
                  <WidgetBotConfigPage />
                </ProtectedRoute>
              } />

              {/* Admin - Omnichannel Conversations (cross-channel inbox + insights) */}
              <Route path="admin/conversations" element={
                <ProtectedRoute requiredRole="admin">
                  <ConversationsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Module Manager (Solo Super Admin) */}
              <Route path="settings/modules" element={
                <ProtectedRoute requiredRole="super_user">
                  <ModuleManager />
                </ProtectedRoute>
              } />
              
              {/* Settings - Redis Admin (Solo Super Admin) */}
              <Route path="settings/redis" element={
                <ProtectedRoute requiredRole="super_user">
                  <Navigate to="/settings/jobs-admin" replace />
                </ProtectedRoute>
              } />

              {/* Settings - Jobs Admin (formerly /settings/redis) */}
              <Route path="settings/jobs-admin" element={
                <ProtectedRoute requiredRole="super_user">
                  <RedisAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - Workers Admin (Admin) */}
              <Route path="settings/workers-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <WorkersAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - System Health (NOC) */}
              <Route path="settings/system-health" element={
                <ProtectedRoute requiredRole="admin">
                  <SystemHealthNocPage />
                </ProtectedRoute>
              } />

              {/* Settings - A2A Message Bus */}
              <Route path="settings/a2a-message-bus" element={
                <ProtectedRoute requiredRole="admin">
                  <A2AMessageBusPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - Roles (Solo Super Admin) */}
              <Route path="settings/roles" element={
                <ProtectedRoute requiredRole="super_user">
                  <RoleManagement />
                </ProtectedRoute>
              } />
              
              {/* Settings - Audit Logs (Solo Super Admin) */}
              <Route path="settings/audit" element={
                <ProtectedRoute requiredRole="super_user">
                  <UnderConstruction />
                </ProtectedRoute>
              } />
              
              {/* Settings - Voice Orchestrator (Admin) */}
              <Route path="settings/voice/:agentId?" element={
                <ProtectedRoute requiredRole="admin">
                  <VoiceAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - Demo Video Generator (Admin) */}
              <Route path="settings/demo-video" element={
                <ProtectedRoute requiredRole="admin" requiredModule="demo_video">
                  <DemoVideoAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Excel Export Generator (Admin) */}
              <Route path="settings/excel-export" element={
                <ProtectedRoute requiredRole="admin">
                  <ExcelExportSettingsPage />
                </ProtectedRoute>
              } />

              {/* Settings - CLU Sentinel (Admin) */}
              <Route path="settings/sentinel/:agentId?" element={
                <ProtectedRoute requiredRole="admin">
                  <SentinelAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Sentinel Ops (Admin) */}
              <Route path="settings/sentinel-ops/:agentId?" element={
                <ProtectedRoute requiredRole="admin">
                  <SentinelOpsAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - LLM Cost Dashboard (Admin) */}
              <Route path="settings/llm-costs" element={
                <ProtectedRoute requiredRole="admin">
                  <LLMCostDashboard />
                </ProtectedRoute>
              } />
              
              {/* Settings - Billing Administration (Admin) */}
              <Route path="billing" element={
                <ProtectedRoute requiredRole="admin">
                  <MyBillingPage />
                </ProtectedRoute>
              } />
              <Route path="settings/billing" element={
                <ProtectedRoute requiredRole="admin">
                  <BillingAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/billing/upgrade" element={
                <ProtectedRoute>
                  <UpgradeBillingPage />
                </ProtectedRoute>
              } />
              <Route path="admin/billing" element={
                <ProtectedRoute requiredRole="admin">
                  <BillingAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - DataWatch CDC */}
              <Route path="settings/datawatch" element={
                <ProtectedRoute requiredRole="super_user">
                  <DataWatchPage />
                </ProtectedRoute>
              } />

              {/* Settings - Dashboard Administration (Admin) */}
              <Route path="settings/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <DashboardAdminPage />
                </ProtectedRoute>
              } />
              <Route path="admin/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <DashboardAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - FBT Administration (Admin) */}
              <Route path="settings/fbt" element={
                <ProtectedRoute requiredRole="admin" requiredModule="fbt">
                  <FBTAdminPage />
                </ProtectedRoute>
              } />
              <Route path="admin/fbt" element={
                <ProtectedRoute requiredRole="admin" requiredModule="fbt">
                  <FBTAdminPage />
                </ProtectedRoute>
              } />
              
              {/* Settings - CLU Financial Intelligence (Admin) */}
              <Route path="settings/clu-financials" element={
                <ProtectedRoute requiredRole="admin">
                  <FinancialIntelligencePage />
                </ProtectedRoute>
              } />

              {/* Settings - ProspectCatch (Admin) */}
              <Route path="settings/prospectcatch" element={
                <ProtectedRoute requiredRole="admin">
                  <ProspectCatchPage />
                </ProtectedRoute>
              } />

              {/* LLM Visibility Suite (Citation Monitor + GEO Engine + Indexing Accelerator) */}
              <Route path="settings/llm-visibility" element={
                <ProtectedRoute requiredRole="admin">
                  <LlmVisibilitySuitePage />
                </ProtectedRoute>
              } />

              {/* Legacy standalone URLs — redirect into the consolidated suite, tab preserved */}
              <Route path="settings/citation-monitor" element={<Navigate to="/settings/llm-visibility?tab=monitor" replace />} />
              <Route path="settings/geo-engine" element={<Navigate to="/settings/llm-visibility?tab=optimize" replace />} />
              <Route path="settings/indexing" element={<Navigate to="/settings/llm-visibility?tab=index" replace />} />

              {/* Settings - Budget Planning (Admin) */}
              <Route path="settings/budget-planning" element={
                <ProtectedRoute requiredRole="admin">
                  <BudgetPlanningPage />
                </ProtectedRoute>
              } />

              {/* Settings - Quantum Readiness (Admin) */}
              <Route path="settings/quantum-readiness" element={
                <ProtectedRoute requiredRole="admin">
                  <QuantumReadinessPage />
                </ProtectedRoute>
              } />

              {/* Settings - Homologation (Admin) */}
              <Route path="settings/homologation" element={
                <ProtectedRoute requiredRole="admin">
                  <HomologationPage />
                </ProtectedRoute>
              } />

              {/* Settings - Reference Values (Admin) */}
              <Route path="settings/reference-values" element={
                <ProtectedRoute requiredRole="admin">
                  <ReferenceValuesPage />
                </ProtectedRoute>
              } />

              {/* Settings - Approval Workflows (Admin) */}
              <Route path="settings/approval-workflows" element={
                <ProtectedRoute requiredRole="admin">
                  <ApprovalWorkflowsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Analyst Studio (Admin) */}
              <Route path="settings/analyst-studio" element={
                <ProtectedRoute requiredRole="admin">
                  <AnalystStudioPage />
                </ProtectedRoute>
              } />

              {/* Settings - Cost Estimator (Admin) */}
              <Route path="settings/cost-estimator" element={
                <ProtectedRoute requiredRole="admin">
                  <CostEstimatorPage />
                </ProtectedRoute>
              } />

              {/* Settings - Quoting (Admin) */}
              <Route path="settings/quoting" element={
                <ProtectedRoute requiredRole="admin">
                  <QuotingPage />
                </ProtectedRoute>
              } />

              {/* Settings - Persona Profile (Admin) — CLU Digital Mentor */}
              <Route path="settings/persona-profile" element={
                <ProtectedRoute requiredRole="admin">
                  <PersonaProfilePage />
                </ProtectedRoute>
              } />

              {/* Settings - Sites (Admin) — CLU Sites */}
              <Route path="settings/sites" element={
                <ProtectedRoute requiredRole="admin">
                  <SitesPage />
                </ProtectedRoute>
              } />

              {/* Settings - Digital Assets (Admin) — signed certificates, DIGITAL_ASSETS_DESIGN.md DA6 */}
              <Route path="settings/digital-assets" element={
                <ProtectedRoute requiredRole="admin">
                  <DigitalAssetsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Digital Mentor (Admin) — checklist + module gate per Agent */}
              <Route path="settings/digital-mentor" element={
                <ProtectedRoute requiredRole="admin">
                  <DigitalMentorPage />
                </ProtectedRoute>
              } />

              {/* Settings - Meeting Agent (everyone; non-admins only get the Meetings tab) */}
              <Route path="settings/meeting-agent" element={
                <ProtectedRoute>
                  <MeetingAgentPage />
                </ProtectedRoute>
              } />

              {/* Settings - Data Intake (Admin) */}
              <Route path="settings/data-intake" element={
                <ProtectedRoute requiredRole="admin">
                  <DataIntakePage />
                </ProtectedRoute>
              } />

              {/* Settings - IoT Telemetry (Admin) */}
              <Route path="settings/iot-telemetry" element={
                <ProtectedRoute requiredRole="admin">
                  <IoTTelemetryPage />
                </ProtectedRoute>
              } />

              {/* Settings - Drive Monitor (Admin) */}
              <Route path="settings/drive-monitor" element={
                <ProtectedRoute requiredRole="admin">
                  <DriveMonitorSettingsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Channels catalog (Admin) — "Channels by default" Phase 1B.
                  NOTE: path is "channels-catalog", not "channels" — "settings/channels"
                  is already taken by ChannelsAdminPage (Gateway & Channels / inbound
                  webhooks admin, see line ~1495), an unrelated existing feature. */}
              <Route path="settings/channels-catalog" element={
                <ProtectedRoute requiredRole="admin">
                  <ChannelsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Banking (Admin) */}
              <Route path="settings/banking" element={
                <ProtectedRoute requiredRole="admin">
                  <BankingPage />
                </ProtectedRoute>
              } />

              {/* Settings - Voice Calls & Campaigns (Admin) */}
              <Route path="settings/voice-campaigns" element={
                <ProtectedRoute requiredRole="admin">
                  <VoiceCallsPage />
                </ProtectedRoute>
              } />

              {/* Settings - ShiftSense (Admin) */}
              <Route path="settings/shiftsense" element={
                <ProtectedRoute requiredRole="admin">
                  <ShiftSensePage />
                </ProtectedRoute>
              } />

              {/* Settings - ISO Product Factory (Admin) */}
              <Route path="settings/iso" element={
                <ProtectedRoute requiredRole="admin">
                  <ProductFactoryPage />
                </ProtectedRoute>
              } />

              {/* Settings - Semantic Ecosystem Index (Admin) */}
              <Route path="settings/semantic-index" element={
                <ProtectedRoute requiredRole="admin">
                  <SemanticIndexPage />
                </ProtectedRoute>
              } />

              {/* Settings - Specialist Council (Admin) */}
              <Route path="settings/council" element={
                <ProtectedRoute requiredRole="admin">
                  <CouncilPage />
                </ProtectedRoute>
              } />
              <Route path="settings/council/new" element={
                <ProtectedRoute requiredRole="admin">
                  <CouncilSpecialistFormPage />
                </ProtectedRoute>
              } />
              <Route path="settings/council/:specialistId" element={
                <ProtectedRoute requiredRole="admin">
                  <CouncilSpecialistDetailPage />
                </ProtectedRoute>
              } />
              <Route path="settings/council/:specialistId/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <CouncilSpecialistFormPage />
                </ProtectedRoute>
              } />

              {/* Settings - Tool Workbench (Admin) */}
              <Route path="settings/tool-workbench" element={
                <ProtectedRoute requiredRole="admin">
                  <ToolWorkbenchPage />
                </ProtectedRoute>
              } />

              {/* Settings - SAP Agent Console: on-prem worker fleet + Reverse Harvester (Admin) */}
              {/* Registered at both slugs on purpose: /settings/local-runtime is the URL the
                  ADMOSA pilot runbook (LOCAL_AGENT_RUNTIME_NEXT_STEPS.md) tells the on-prem
                  operator to check — do not remove it. /settings/sap-agent is the new canonical
                  nav entry (see Settings.jsx). */}
              <Route path="settings/sap-agent" element={
                <ProtectedRoute requiredRole="admin">
                  <LocalRuntimePage />
                </ProtectedRoute>
              } />
              <Route path="settings/local-runtime" element={
                <ProtectedRoute requiredRole="admin">
                  <LocalRuntimePage />
                </ProtectedRoute>
              } />

              {/* CLU Studio — self-service agent builder (new front door, Path A) */}
              <Route path="studio" element={
                <ProtectedRoute>
                  <StudioPage />
                </ProtectedRoute>
              } />

              {/* Process Studio (Admin) */}
              <Route path="process-studio" element={
                <ProtectedRoute requiredRole="admin">
                  <ProcessStudioPage />
                </ProtectedRoute>
              } />
              <Route path="process-studio/builder/:processId" element={
                <ProtectedRoute requiredRole="admin">
                  <ProcessBuilderPage />
                </ProtectedRoute>
              } />
              <Route path="process-studio/:processId" element={
                <ProtectedRoute requiredRole="admin">
                  <ProcessDetailPage />
                </ProtectedRoute>
              } />

              {/* My Memory — all authenticated users */}
              <Route path="settings/my-memory" element={<MyMemoryPage />} />

              {/* Settings - Compliance Agent (Admin) */}
              <Route path="settings/compliance" element={
                <ProtectedRoute requiredRole="admin">
                  <CompliancePage />
                </ProtectedRoute>
              } />

              {/* Settings - CLU Skills (Admin) */}
              <Route path="settings/skills" element={
                <ProtectedRoute requiredRole="admin">
                  <SkillsPage />
                </ProtectedRoute>
              } />

              {/* Settings - CLU NG Scene Symbols (Admin) */}
              <Route path="settings/scene-symbols" element={
                <ProtectedRoute requiredRole="admin">
                  <SceneSymbolsPage />
                </ProtectedRoute>
              } />

              {/* Settings - Conversation Capture Engine (Admin) */}
              <Route path="settings/capture-targets" element={
                <ProtectedRoute requiredRole="admin">
                  <CaptureEnginePage />
                </ProtectedRoute>
              } />

              {/* Settings - AutoDemo (Admin) */}
              <Route path="settings/autodemo" element={
                <ProtectedRoute requiredRole="admin">
                  <AutoDemoSettingsPage />
                </ProtectedRoute>
              } />

              {/* Settings - MCP Gateway (Admin) */}
              <Route path="settings/mcp-gateway" element={
                <ProtectedRoute requiredRole="admin">
                  <MCPGatewayPage />
                </ProtectedRoute>
              } />

              {/* Settings - DB Connections Tables (Admin) — agent-first entry:
                  selects an agent via the central AgentSelector, resolves its
                  connection, and lands on the same Tables view as
                  /db-connections/:connectionId/tables. Connection CRUD
                  (create/edit/delete/test) stays reachable via Agent
                  Management → BigQuery tab (DBConnectionsManagement), just no
                  longer the mandatory first stop. */}
              <Route path="settings/db-connections" element={
                <ProtectedRoute requiredRole="admin">
                  <TablesPage />
                </ProtectedRoute>
              } />

              {/* Internal Module Page - authenticated, serves dynamic Poiesis modules by agent + route slug */}
              <Route path="modules/:agentSlug/*" element={<InternalModulePage />} />

              {/* Settings - Poiesis Admin (Admin) */}
              <Route path="settings/poiesis" element={
                <ProtectedRoute requiredRole="admin">
                  <PoiesisAdminPage />
                </ProtectedRoute>
              } />

              {/* Settings - Poiesis Software Factory (Admin) */}
              <Route path="settings/poiesis/requests/:requestId" element={
                <ProtectedRoute requiredRole="admin">
                  <PoiesisRequestDetail />
                </ProtectedRoute>
              } />

              {/* Settings - Poiesis Module Factory (Admin) */}
              <Route path="settings/poiesis-modules" element={<Navigate to="/settings/poiesis" replace />} />

              {/* Settings - Poiesis Builder — redirects to unified Poiesis page */}
              <Route path="settings/poiesis-builder" element={<Navigate to="/settings/poiesis" replace />} />

              {/* Settings - Identity Verification Config (Admin) */}
              <Route path="settings/otp" element={
                <ProtectedRoute requiredRole="admin">
                  <OTPConfigPage />
                </ProtectedRoute>
              } />

              {/* Public — Face Verification web/embed handoff (no auth — verificationId is the bearer credential) */}
              <Route path="face-verify-web/:verificationId" element={<FaceVerifyPublic />} />

              {/* Mobile - Face Verification (no auth required — token-scoped).
                  Legacy iframe-wrapper page: redirects to the real public page instead of
                  iframing `/face-verify/embed/:token`, a route that never existed — the iframe
                  fell through to the SPA's own index.html and booted a second full app instance
                  inside it, indefinitely, whether or not anyone interacted with the module. */}
              <Route path="m/verify/face/:token" element={<FaceVerifyTokenRedirect />} />

              {/* Settings - Validation Agent Rules (Admin) */}
              <Route path="settings/validation-rules" element={
                <ProtectedRoute requiredRole="admin">
                  <ValidationRulesPage />
                </ProtectedRoute>
              } />

              {/* Settings - Tool Learning Loop "My Learnings" (any authenticated user —
                  TOOL_LEARNING_LOOP_ARCHITECTURE.md PR-6, not admin-gated: it's the
                  individual user's own view of what CLU learned from their corrections) */}
              <Route path="settings/tool-learning" element={
                <ProtectedRoute>
                  <ToolLearningPage />
                </ProtectedRoute>
              } />

              {/* Settings - Marketplace: hidden, old links land on the Ecosystem overview */}
              <Route path="settings/marketplace" element={<Navigate to="/clerks-ecosystem" replace />} />

              {/* Settings - SQL Test Panel (Solo Super Admin) */}
              <Route path="settings/sqltest" element={
                <ProtectedRoute requiredRole="super_user">
                  <SQLTestPanel />
                </ProtectedRoute>
              } />

              {/* Settings - LLM Models Registry (Super User) */}
              <Route path="settings/llm-models" element={
                <ProtectedRoute requiredRole="super_user">
                  <LLMModelsManager />
                </ProtectedRoute>
              } />

              {/* Settings - Settings Manager */}
              <Route path="settings/system-settings" element={
                <ProtectedRoute requiredRole="super_user">
                  <SettingsManager />
                </ProtectedRoute>
              } />

              {/* User Drive — accessible to all authenticated users */}
              <Route path="drive" element={<ProtectedRoute><UserDrivePage /></ProtectedRoute>} />
              <Route path="drive/:agentId" element={<ProtectedRoute><UserDrivePage /></ProtectedRoute>} />

              {/* Settings - GCS File Manager (super_user only) */}
              <Route path="settings/file-manager" element={
                <ProtectedRoute requiredRole="super_user">
                  <GCSFileManager />
                </ProtectedRoute>
              } />

              {/* Settings - Output Artifacts (what a Clerk delivers) */}
              <Route path="settings/output-artifacts" element={
                <ProtectedRoute requiredRole="admin">
                  <ArtifactTemplatesPage />
                </ProtectedRoute>
              } />

              {/* Settings - SQL Console Server (Solo Super Admin) */}
              <Route path="settings/sqlconsoleserver" element={
                <ProtectedRoute requiredRole="super_user">
                  <SQLConsoleServer />
                </ProtectedRoute>
              } />

              {/* Settings - RLS Management (Solo Super Admin) */}
              <Route path="settings/rls" element={
                <ProtectedRoute requiredRole="super_user">
                  <RLSManagement />
                </ProtectedRoute>
              } />

              {/* Settings - SQLQueries Vault (Admin) */}
              <Route path="settings/sql-queries-vault" element={
                <ProtectedRoute requiredRole="admin">
                  <SQLQueriesVault />
                </ProtectedRoute>
              } />

              {/* Settings - Tools Audit (Admin) */}
              <Route path="settings/tools-audit" element={
                <ProtectedRoute requiredRole="admin">
                  <ToolsAudit />
                </ProtectedRoute>
              } />
              
              {/* Settings - Worker Metrics Dashboard (Admin) - Now integrated in Workers Admin */}
              {/* <Route path="settings/worker-metrics" element={
                <ProtectedRoute requiredRole="admin">
                  <WorkerMetricsDashboard />
                </ProtectedRoute>
              } /> */}

              {/* Settings - Orchestrator Debug Dashboard (Solo Super User) */}
              <Route path="settings/orchestrator-debug" element={
                <ProtectedRoute requiredRole="super_user">
                  <OrchestratorDebugDashboard />
                </ProtectedRoute>
              } />

              {/* Settings - Agents Connection Admin (Solo Super User) */}
              <Route path="settings/tml-admin" element={
                <ProtectedRoute requiredRole="super_user">
                  <TMLAdmin />
                </ProtectedRoute>
              } />

              {/* Settings - Tool Builder V2 (Clerks Ecosystem Form) */}
              <Route path="settings/tool-builder-v2" element={
                <ProtectedRoute requiredRole="super_user">
                  <ClerksEcosystemPage />
                </ProtectedRoute>
              } />

              {/* Settings - Connectors Library */}
              <Route path="settings/connectors-library" element={
                <ProtectedRoute requiredRole="admin">
                  <ConnectorsLibrary />
                </ProtectedRoute>
              } />

              {/* Settings - Connector Catalog (asset_catalog overview) */}
              <Route path="settings/connector-catalog" element={
                <ProtectedRoute requiredRole="admin">
                  <ConnectorCatalogPage />
                </ProtectedRoute>
              } />

              {/* Settings - Ecosystem Visibility (public/private scoping) */}
              <Route path="settings/ecosystem-visibility" element={
                <ProtectedRoute requiredRole="admin">
                  <EcosystemVisibilityPage />
                </ProtectedRoute>
              } />

              {/* Settings - Connector Runner (guided run for any connector) */}
              <Route path="settings/connector-runner" element={
                <ProtectedRoute requiredRole="admin">
                  <ConnectorRunnerPage />
                </ProtectedRoute>
              } />

              {/* Settings - Agent Card Viewer */}
              <Route path="settings/agent-card/:connectorId" element={
                <ProtectedRoute requiredRole="admin">
                  <ToolCardViewer />
                </ProtectedRoute>
              } />

              {/* Settings - Entity Extraction Admin */}
              <Route path="settings/entity-extraction" element={
                <ProtectedRoute requiredRole="admin">
                  <EntityExtractionAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/video-pipeline" element={
                <ProtectedRoute requiredRole="admin">
                  <VideoPipelinePage />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction" element={
                <ProtectedRoute requiredRole="admin">
                  <EntityExtractionAdminPage />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/patterns/new" element={
                <ProtectedRoute requiredRole="super_user">
                  <EntityExtractionPatternEditor />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/patterns/:patternId" element={
                <ProtectedRoute requiredRole="super_user">
                  <EntityExtractionPatternEditor />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/entity-types/new" element={
                <ProtectedRoute requiredRole="super_user">
                  <EntityTypeEditor />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/entity-types/:entityType" element={
                <ProtectedRoute requiredRole="super_user">
                  <EntityTypeEditor />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/extractors/new" element={
                <ProtectedRoute requiredRole="super_user">
                  <ExtractorEditor />
                </ProtectedRoute>
              } />
              <Route path="admin/entity-extraction/extractors/:toolPattern" element={
                <ProtectedRoute requiredRole="super_user">
                  <ExtractorEditor />
                </ProtectedRoute>
              } />

              {/* CLU Admin (Super User) */}
              <Route path="settings/clu-admin" element={
                <ProtectedRoute requiredRole="super_user">
                  <CLUAdminPage />
                </ProtectedRoute>
              } />

              {/* Observation Engine Admin (Admin/Super User) */}
              <Route path="settings/observation-admin" element={
                <ProtectedRoute requiredRole="admin">
                  <ObservationAdminPage />
                </ProtectedRoute>
              } />

              {/* Scheduler Control Panel - Moved to Automation Center */}
              {/* Access via /admin/automation tab 'scheduler' */}
              <Route path="settings/scheduler-control" element={
                <ProtectedRoute requiredRole="admin">
                  <Navigate to="/admin/automation" replace />
                </ProtectedRoute>
              } />

              {/* Control Tower — Unified Audit */}
              <Route path="admin/control-tower" element={
                <ProtectedRoute requiredRole="admin">
                  <ControlTowerPage />
                </ProtectedRoute>
              } />

              {/* Deploy Engine — Custom CI/CD builds panel (super_user only) */}
              <Route path="admin/deploys" element={
                <ProtectedRoute requiredRole="super_user">
                  <DeployEnginePage />
                </ProtectedRoute>
              } />

              {/* Memory & Learning System Admin (Admin) */}
              <Route path="admin/memory-learning" element={
                <ProtectedRoute requiredRole="admin">
                  <MemoryLearningAdminPage />
                </ProtectedRoute>
              } />
              <Route path="settings/memory-learning" element={
                <ProtectedRoute requiredRole="admin">
                  <MemoryLearningAdminPage />
                </ProtectedRoute>
              } />


              {/* AA Setup Wizard (Admin) */}
              <Route path="settings/aa-setup/new" element={<ProtectedRoute requiredRole="admin"><AASetupWizardPage /></ProtectedRoute>} />
              <Route path="settings/aa-setup/:smartagentId" element={<ProtectedRoute requiredRole="admin"><AASetupWizardPage /></ProtectedRoute>} />

              {/* SmartAgents Designer (Admin) */}
              <Route path="settings/smartagents" element={
                <ProtectedRoute requiredRole="admin">
                  <SmartAgentDesigner />
                </ProtectedRoute>
              } />
              <Route path="admin/smartagents" element={
                <ProtectedRoute requiredRole="admin">
                  <SmartAgentDesigner />
                </ProtectedRoute>
              } />
              
              {/* SmartAgents Testing Panel (Admin/Super User) */}
              <Route path="admin/smartagents/testing" element={
                <ProtectedRoute requiredRole={['admin', 'super_user']}>
                  <SmartAgentTestingPanel />
                </ProtectedRoute>
              } />
              <Route path="settings/smartagents/testing" element={
                <ProtectedRoute requiredRole={['admin', 'super_user']}>
                  <SmartAgentTestingPanel />
                </ProtectedRoute>
              } />


              {/* Settings - Dynamic Prompt Variables Manager (Solo Super User) */}
              <Route path="settings/prompt-variables" element={
                <ProtectedRoute requiredRole="super_user">
                  <DynamicPromptVariablesManager />
                </ProtectedRoute>
              } />

              {/* Prompt Library V2 (official) */}
              <Route
                path="settings/prompt-library"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <PromptLibraryPage />
                  </ProtectedRoute>
                }
              />


              {/* Settings - Biometrics (Full Face Verify Module) */}
              <Route path="settings/biometrics/:agentId?" element={
                <ProtectedRoute requiredRole="admin">
                  <FaceVerifyHome />
                </ProtectedRoute>
              } />
              <Route path="settings/biometrics" element={
                <ProtectedRoute requiredRole="admin">
                  <FaceVerifyHome />
                </ProtectedRoute>
              } />

              {/* Settings - Face Verify Policy Admin (kept for direct access) */}
              <Route path="settings/face-verify-policy" element={
                <ProtectedRoute requiredRole="admin">
                  <FaceVerifyPolicyAdmin />
                </ProtectedRoute>
              } />

              {/* SOA (Security Officer Agent) Routes */}
              <Route path="admin/soa" element={
                <ProtectedRoute requiredRole="admin">
                  <SOADashboard />
                </ProtectedRoute>
              } />
              {/* Some SOA UI buttons navigate here; keep route to avoid falling back to Clerks/login */}
              <Route path="admin/soa/jobs" element={
                <ProtectedRoute requiredRole="admin">
                  <SOADashboard />
                </ProtectedRoute>
              } />
              <Route path="admin/soa/consents" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAConsentManager />
                </ProtectedRoute>
              } />
              <Route path="settings/soa" element={
                <ProtectedRoute requiredRole="admin">
                  <SOADashboard />
                </ProtectedRoute>
              } />
              <Route path="settings/soa/jobs" element={
                <ProtectedRoute requiredRole="admin">
                  <SOADashboard />
                </ProtectedRoute>
              } />
              <Route path="settings/soa/consents" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAConsentManager />
                </ProtectedRoute>
              } />
              <Route path="settings/soa/profiles" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAProfileManager />
                </ProtectedRoute>
              } />
              {/* Settings - HyperCore Admin (HYPER_PROFILE + HYPER_MESSAGE) */}
              <Route path="settings/hyper-core" element={
                <ProtectedRoute requiredRole="admin">
                  <HyperCoreAdmin />
                </ProtectedRoute>
              } />
              {/* Settings - AtomicHyperPersonalization Admin */}
              <Route path="settings/hyper-core/atomic-hyperpersonalization" element={
                <ProtectedRoute requiredRole="admin">
                  <AtomicHyperPersonalizationAdmin />
                </ProtectedRoute>
              } />
              {/* Settings - Knowledge Source Registry (CIE HyperPersonalizer) */}
              <Route path="settings/hyper-core/knowledge-sources" element={
                <ProtectedRoute requiredRole="admin">
                  <KnowledgeSourceRegistryPage />
                </ProtectedRoute>
              } />

              {/* GRI - GRID Room Intelligence */}
              <Route path="settings/gri/livekit" element={
                <ProtectedRoute requiredRole="admin">
                  <GRILiveKitConfigPage />
                </ProtectedRoute>
              } />
              <Route path="settings/gri/rooms" element={
                <ProtectedRoute requiredRole="admin">
                  <GRIRoomsAdminPage />
                </ProtectedRoute>
              } />
              <Route path="gri/room/:roomId/join" element={
                <ProtectedRoute requiredRole="user">
                  <GRIRoomJoinPage />
                </ProtectedRoute>
              } />
              <Route path="admin/soa/profiles" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAProfileManager />
                </ProtectedRoute>
              } />
              {/* SOAProfileManager "View" button navigates to /admin/soa/profiles/:profileId */}
              <Route path="admin/soa/profiles/:profileId" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAProfileManager />
                </ProtectedRoute>
              } />
              <Route path="settings/soa/profiles/:profileId" element={
                <ProtectedRoute requiredRole="admin">
                  <SOAProfileManager />
                </ProtectedRoute>
              } />

              {/* Infrastructure Monitoring Routes */}
              <Route path="admin/monitoring" element={
                <ProtectedRoute requiredRole="admin">
                  <MonitoringDashboardLayout />
                </ProtectedRoute>
              }>
                <Route path="infrastructure" element={<InfraOverviewDashboard />} />
                <Route path="servers" element={<ServerDetailDashboard />} />
                <Route path="sap" element={<SAPDashboard />} />
                <Route path="sap/jobs" element={<SAPJobMonitorDashboard />} />
                <Route path="sap/interfaces" element={<SAPInterfaceDashboard />} />
                <Route path="alerts" element={<AlertManagementDashboard />} />
              </Route>
              <Route path="settings/monitoring" element={
                <ProtectedRoute requiredRole="admin">
                  <MonitoringAdminPage />
                </ProtectedRoute>
              } />

              {/* Automation Center - Unified (Loops, Webhooks, Scheduling) */}
              <Route path="admin/automation" element={
                <ProtectedRoute requiredRole="admin">
                  <AutomationCenter />
                </ProtectedRoute>
              } />

              <Route path="admin/bug-reports" element={
                <ProtectedRoute requiredRole="super_user">
                  <BugReportsAdminPage />
                </ProtectedRoute>
              } />

              {/* Galaxia Neural Swarm — data is tenant-scoped server-side (§20a) */}
              <Route path="galaxia" element={
                <ProtectedRoute>
                  <ConstellationPage />
                </ProtectedRoute>
              } />

              {/* Rules Evaluation Module */}
              <Route path="rules-evaluation" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesEvaluationHome />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetList />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/new" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetCreateVisual />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/create" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetCreateVisual />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetView />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/:id/edit" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetEdit />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/:id/test" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetTester />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/dashboard" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesEvaluationDashboard />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/:id/versions" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetVersionManager />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/rulesets/:id/ab-testing" element={
                <ProtectedRoute requiredRole="admin">
                  <RulesetABTesting />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/executions" element={
                <ProtectedRoute requiredRole="admin">
                  <ExecutionHistory />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/executions/:id" element={
                <ProtectedRoute requiredRole="admin">
                  <ExecutionDetail />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/agents" element={
                <ProtectedRoute requiredRole="admin">
                  <AgentRulesetAssociation />
                </ProtectedRoute>
              } />
              <Route path="rules-evaluation/testing" element={
                <ProtectedRoute requiredRole="admin">
                  <TestingTools />
                </ProtectedRoute>
              } />

            </Route>

            {/* Public Survey Routes - No authentication required */}
            <Route
              path="survey/public/:surveyId"
              element={<SurveyLandingPage />}
            />
            <Route
              path="survey/:surveyId"
              element={<SurveyLandingPage />}
            />
            <Route
              path="survey/:launchId"
              element={<SurveyLandingPage />}
            />

            {/* CLU Autodemo — fullscreen protected route, no Layout wrapper */}
            <Route
              path="autodemo"
              element={
                <ProtectedRoute>
                  <AutoDemoPage />
                </ProtectedRoute>
              }
            />

            {/* Quorra Commerce Admin is NOT here — it moved inside the CLU shell
                block above, so it renders with the sidebar and header like every
                other module. Only the public storefront stays outside. */}

            {/* Quorra Public Storefront — no Firebase auth required */}
            <Route path="store/:slug" element={<StorefrontPage />} />
            {/* Same page, with a product pre-selected — the shareable "ficha de
                producto" link (see backend/src/services/commerce/catalog_service.py
                build_product_url). Same component, StorefrontPage reads the extra
                :productId param to open ProductDetailModal on load. */}
            <Route path="store/:slug/producto/:productId" element={<StorefrontPage />} />
            {/* The form a purchase's Skill sends the buyer when it needs more
                (QUORRA_PURCHASE_TRIGGERS.md, T4): public, behind a signed link. */}
            <Route path="store/:slug/complete/:ref" element={<PurchaseInputPage />} />

            {/* Catch-all route - Redirecciona a login para rutas desconocidas */}
            <Route path="*" element={<Navigate to="/firebase-login" replace />} />
          </Routes>
          </Suspense>
        </RoleProvider>
        </WorkspaceProvider>
        <GlobalStepUpModal />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
